import { getTotalClassLevel } from '../../../shared/rules/classIdentity.mjs';
import { recklessEligibility } from '../../../shared/rules/recklessAttack.mjs';
import { speciesUseProfile, speciesRevision, mergeSpeciesResources } from '../../../shared/rules/speciesResources.mjs';
import SpellRecoveryControls from '../components/SpellRecoveryControls';
import { getSecondWindProfile, getActionSurgeProfile } from '../../../shared/rules/fighter.mjs';
import { FIGHTING_STYLE_CLASSES, getClassFightingStyle, getStyleCantripGrants, ADDITIONAL_FIGHTING_STYLE_FEATURE_ID, getAdditionalFightingStyle } from '../../../shared/rules/fightingStyle.mjs';
import spellCatalog from '../../../data/spells.json';
import { FAVORED_ENEMY_GRANT_ID, favoredEnemyUses, getRangerLevel } from '../../../shared/rules/ranger.mjs';
import { getDefensiveTactics, getHuntersPrey, DEFENSIVE_TACTICS_FEATURE_ID, HUNTERS_PREY_FEATURE_ID } from '../../../shared/rules/subclasses/ranger/huntersPrey.mjs';
import FightingStyleChooser from './FightingStyleChooser';
import HuntersPreyChooser, { DefensiveTacticsChooser } from './HuntersPreyChooser';
import { BlessedStrikesChooser, DivineOrderChooser } from './ClericChoiceChooser';
import { ElementalFuryChooser, KnownFormsChooser, LandChooser, PrimalOrderChooser } from './DruidChoiceChooser';
import { BonusProficienciesChooser, MagicalDiscoveriesChooser } from './LoreChoiceChooser';
import { BONUS_PROFICIENCIES_FEATURE_ID, MAGICAL_DISCOVERIES_FEATURE_ID, PEERLESS_SKILL_FEATURE_ID, CUTTING_WORDS_FEATURE_ID, discoveryGrantId, getBonusProficiencies, getMagicalDiscoveries } from '../../../shared/rules/subclasses/bard/lore.mjs';
import WizardSpellbook, { SignatureSpellsChooser, SpellMasteryChooser } from './WizardSpellbook';
import { getDivineOrder } from '../../../shared/rules/divineOrder';
import { getPrimalOrder } from '../../../shared/rules/primalOrder';
import { getElementalFury } from '../../../shared/rules/druid.mjs';
import { getKnownForms } from '../../../shared/rules/beastForm';
import { CIRCLE_SPELLS_FEATURE_ID, LANDS_AID_FEATURE_ID, NATURAL_RECOVERY_FEATURE_ID, getLand } from '../../../shared/rules/subclasses/druid/land.mjs';
import { getBlessedStrikes } from '../../../shared/rules/cleric.mjs';
import { getSignatureSpells, getSpellMastery } from '../../../shared/rules/wizard.mjs';
import { LIFE_DOMAIN_SPELLS, domainSpellGrantId, getLifeDomainSpells } from '../../../shared/rules/subclasses/cleric/life.mjs';
import MagicInitiateChooser from './MagicInitiateChooser';
import { MAGIC_INITIATE_LISTS, getMagicInitiateLists, getMagicInitiateGrants, getMagicInitiateChoice, magicInitiateFreeCastsRemaining, MAGIC_INITIATE_FREE_CASTS } from '../../../shared/rules/magicInitiate.mjs';
import { getSubclassFeatureEntitlements } from '../../../shared/rules/subclasses/index.mjs';
import { getDevotionOathSpells, oathSpellGrantId } from '../../../shared/rules/subclasses/paladin/devotion.mjs';
import { getBuffSpellDefinition } from '../../../shared/rules/effects/buffSpellDefinitions.mjs';
import { getHealingSpellDefinition } from '../../../shared/rules/effects/healingSpellDefinitions.mjs';
import { FAITHFUL_STEED_GRANT_ID, FIND_STEED_SPELL_ID, faithfulSteedFreeRemaining, hasPaladinFeature } from '../../../shared/rules/paladin.mjs';
import { isClassFeatureDisplayable } from '../../../shared/rules/classFeatureSupport.mjs';
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Modal, Card, Button, Spinner, Form, Badge } from 'react-bootstrap';
import apiFetch from '../../../utils/apiFetch';
import FeatureModal from './FeatureModal';
import UpcastModal from './UpcastModal';
import actionSurgeIcon from '../../../images/action-surge-icon.png';
import dragonWingsIcon from '../../../images/dragon-wings-icon.png';
import adrenalineRushIcon from '../../../images/adrenaline-rush.png';
import speakWithAnimalIcon from '../../../images/speak-with-animal.png';
import proficiencyBonus from '../../../utils/proficiencyBonus';
import DockControls from '../components/DockControls';
import { Swords } from 'lucide-react';
import {
  getAvailableBarbarianFeatures,
  canActivateRecklessAttack,
  getBarbarianLevel,
  getWeaponMasteryState,
  setWeaponMasterySelections,
  validateBarbarianWeaponMasteries,
} from '../utils/barbarian';
import { getClassLevel, getWeaponMasteryEntitlements, getSelectionsForSource } from '../utils/weaponMastery';
import { getPendingFeatureChoices, getEditableFeatureChoices, weaponMasteryChoiceKey, fightingStyleChoiceKey, ADDITIONAL_FIGHTING_STYLE_CHOICE_KEY, DIVINE_ORDER_CHOICE_KEY, BLESSED_STRIKES_CHOICE_KEY, PRIMAL_ORDER_CHOICE_KEY, ELEMENTAL_FURY_CHOICE_KEY, KNOWN_FORMS_CHOICE_KEY, LAND_CHOICE_KEY, BONUS_PROFICIENCIES_CHOICE_KEY, MAGICAL_DISCOVERIES_CHOICE_KEY, SPELLBOOK_CHOICE_KEY, SPELL_MASTERY_CHOICE_KEY, SIGNATURE_SPELLS_CHOICE_KEY, HUNTERS_PREY_CHOICE_KEY, DEFENSIVE_TACTICS_CHOICE_KEY, magicInitiateChoiceKey } from '../utils/featureChoices';

const LINEAGE_SPELLS = {
  'elf-drow-dancing-lights': {
    spellName: 'Dancing Lights',
    baseLevel: 0,
    castingTime: '1 action',
  },
  'elf-drow-faerie-fire': {
    spellName: 'Faerie Fire',
    maxUses: 1,
    baseLevel: 1,
    castingTime: '1 action',
  },
  'elf-drow-darkness': {
    spellName: 'Darkness',
    maxUses: 1,
    baseLevel: 2,
    castingTime: '1 action',
  },
  'elf-high-prestidigitation': {
    spellName: 'Prestidigitation',
    baseLevel: 0,
    castingTime: '1 action',
  },
  'elf-high-detect-magic': {
    spellName: 'Detect Magic',
    maxUses: 1,
    baseLevel: 1,
    castingTime: '1 action',
  },
  'elf-high-misty-step': {
    spellName: 'Misty Step',
    maxUses: 1,
    baseLevel: 2,
    castingTime: '1 bonus action',
  },
  'elf-wood-druidcraft': {
    spellName: 'Druidcraft',
    baseLevel: 0,
    castingTime: '1 action',
  },
  'elf-wood-longstrider': {
    spellName: 'Longstrider',
    maxUses: 1,
    baseLevel: 1,
    castingTime: '1 action',
  },
  'elf-wood-pass-without-trace': {
    spellName: 'Pass without Trace',
    maxUses: 1,
    baseLevel: 2,
    castingTime: '1 action',
  },
  'tiefling-abyssal-ray-of-sickness': {
    spellName: 'Ray of Sickness',
    maxUses: 1,
    baseLevel: 1,
    castingTime: '1 action',
  },
  'tiefling-abyssal-hold-person': {
    spellName: 'Hold Person',
    maxUses: 1,
    baseLevel: 2,
    castingTime: '1 action',
  },
  'tiefling-chthonic-false-life': {
    spellName: 'False Life',
    maxUses: 1,
    baseLevel: 1,
    castingTime: '1 action',
  },
  'tiefling-chthonic-ray-of-enfeeblement': {
    spellName: 'Ray of Enfeeblement',
    maxUses: 1,
    baseLevel: 2,
    castingTime: '1 action',
  },
  'tiefling-infernal-hellish-rebuke': {
    spellName: 'Hellish Rebuke',
    maxUses: 1,
    baseLevel: 1,
    castingTime: 'Reaction',
  },
  'tiefling-infernal-darkness': {
    spellName: 'Darkness',
    maxUses: 1,
    baseLevel: 2,
    castingTime: '1 action',
  },
};

const LIMITED_USE_LINEAGE_SPELL_IDS = Object.entries(LINEAGE_SPELLS)
  .filter(([, config]) => Number.isFinite(config?.maxUses) && config.maxUses > 0)
  .map(([spellId]) => spellId);

const LIMITED_USE_LINEAGE_SPELL_IDS_SET = new Set(
  LIMITED_USE_LINEAGE_SPELL_IDS
);

// The configurable choice a feature card edits (see utils/featureChoices), or null.
const featureChoiceKey = (feat) => (feat?.isWeaponMasteryConfig
  ? weaponMasteryChoiceKey(feat.masterySource || 'barbarian')
  : feat?.isFightingStyleConfig ? (feat.fightingStyleSlot === 'additional' ? ADDITIONAL_FIGHTING_STYLE_CHOICE_KEY : fightingStyleChoiceKey(feat.fightingStyleClass || 'fighter'))
    : feat?.isHuntersPreyConfig ? HUNTERS_PREY_CHOICE_KEY
    : feat?.isDefensiveTacticsConfig ? DEFENSIVE_TACTICS_CHOICE_KEY
    : feat?.isDivineOrderConfig ? DIVINE_ORDER_CHOICE_KEY
    : feat?.isBlessedStrikesConfig ? BLESSED_STRIKES_CHOICE_KEY
    : feat?.isPrimalOrderConfig ? PRIMAL_ORDER_CHOICE_KEY
    : feat?.isElementalFuryConfig ? ELEMENTAL_FURY_CHOICE_KEY
    : feat?.isKnownFormsConfig ? KNOWN_FORMS_CHOICE_KEY
    : feat?.isLandConfig ? LAND_CHOICE_KEY
    : feat?.isSpellbookConfig ? SPELLBOOK_CHOICE_KEY
    : feat?.isSpellMasteryConfig ? SPELL_MASTERY_CHOICE_KEY
    : feat?.isSignatureSpellsConfig ? SIGNATURE_SPELLS_CHOICE_KEY
    : feat?.isBonusProficienciesConfig ? BONUS_PROFICIENCIES_CHOICE_KEY
    : feat?.isMagicalDiscoveriesConfig ? MAGICAL_DISCOVERIES_CHOICE_KEY
      : feat?.isMagicInitiateConfig ? magicInitiateChoiceKey(feat.magicInitiateList) : null);

export default function Features({
  form,
  showFeatures,
  handleCloseFeatures,
  onAdrenalineRush,
  onDraconicFlight,
  onCastSpell,
  longRestCount,
  shortRestCount,
  availableSlots = { regular: {}, warlock: {} },
  isDocked = false,
  dockedSide = null,
  onDockClose,
  onDockChange,
  characterId,
  onCharacterChange,
  combatState,
}) {
  const [features, setFeatures] = useState([]);
  const [modalFeature, setModalFeature] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const surgeUsed = getActionSurgeProfile(form).remaining === 0;
  const reckless = recklessEligibility(form, combatState, characterId || form?._id || form?.characterId);
  const [speciesUseBusy, setSpeciesUseBusy] = useState(false);
  const speciesUseRequest = useRef(null);
  const speciesUseInFlight = useRef(false);
  const [showUpcast, setShowUpcast] = useState(false);
  const [pendingSpell, setPendingSpell] = useState(null);
  const [weaponData, setWeaponData] = useState({});
  const [abilitySearch, setAbilitySearch] = useState('');
  const [activeAbilityFilter, setActiveAbilityFilter] = useState('all');
  const [abilitySort, setAbilitySort] = useState('class');
  const [selectedAbilityKey, setSelectedAbilityKey] = useState(null);
  const [recentAbilityKeys, setRecentAbilityKeys] = useState([]);
  const speakWithAnimalsUsesRef = useRef(0);
  const lineageSpellUsesRef = useRef({});
  // Faithful Steed's free cast count, read by the open cast modal.
  const faithfulSteedRemainingRef = useRef(0);
  faithfulSteedRemainingRef.current = faithfulSteedFreeRemaining(form);
  const magicInitiateFormRef = useRef(form);
  magicInitiateFormRef.current = form;

  const totalCharacterLevel = useMemo(() => {
    if (!Array.isArray(form?.occupation)) return 0;
    return form.occupation.reduce((sum, occ) => {
      if (typeof occ !== 'object' || occ === null) return sum;
      const levelValue =
        Number(occ.Level ?? occ.level ?? occ.Levels ?? occ.levels ?? 0) || 0;
      return sum + levelValue;
    }, 0);
  }, [form?.occupation]);


  const barbarianLevel = useMemo(() => getBarbarianLevel(form), [form]);
  const weaponMasteryState = useMemo(
    () => getWeaponMasteryState(form),
    [form]
  );
  const eligibleWeaponMasteryOptions = useMemo(() => {
    const byKey = new Map();
    Object.entries(weaponData || {}).forEach(([key, weapon]) => {
      const normalizedKey = String(weapon?.type || key || '').trim().toLowerCase();
      const category = String(weapon?.category || '').trim().toLowerCase();
      if (
        !normalizedKey ||
        byKey.has(normalizedKey) ||
        (category !== 'simple melee' && category !== 'martial melee')
      ) {
        return;
      }
      byKey.set(normalizedKey, {
        key: normalizedKey,
        name: weapon?.name || normalizedKey,
        category,
        mastery: weapon?.mastery,
      });
    });
    return Array.from(byKey.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [weaponData]);
  const eligibleWeaponMasteryMap = useMemo(
    () => new Map(eligibleWeaponMasteryOptions.map((weapon) => [weapon.key, weapon])),
    [eligibleWeaponMasteryOptions]
  );
  const masteryEntitlements = useMemo(
    () => getWeaponMasteryEntitlements(form, weaponData),
    [form, weaponData]
  );
  const hasWeaponMasteryEntitlement = masteryEntitlements.length > 0;
  const pendingFeatureChoices = useMemo(() => getPendingFeatureChoices(form), [form]);
  const editableFeatureChoices = useMemo(() => getEditableFeatureChoices(form), [form]);

  const profBonus = useMemo(() => {
    const provided = Number(form?.proficiencyBonus);
    if (Number.isFinite(provided) && provided > 0) {
      return provided;
    }
    return proficiencyBonus(totalCharacterLevel);
  }, [form?.proficiencyBonus, totalCharacterLevel]);

  const speciesProficiency = proficiencyBonus(getTotalClassLevel(form));
  const adrenalineRushProfile = speciesUseProfile(form, 'orc-adrenaline-rush', speciesProficiency);
  const adrenalineRushUses = adrenalineRushProfile.remaining;
  const adrenalineRushMaxUses = adrenalineRushProfile.maximum;
  const largeFormUsed = speciesUseProfile(form, 'goliath-large-form', speciesProficiency).remaining === 0;
  const draconicFlightUsed = speciesUseProfile(form, 'dragonborn-draconic-flight', speciesProficiency).remaining === 0;
  const relentlessEnduranceUsed = speciesUseProfile(form, 'orc-relentless-endurance', speciesProficiency).remaining === 0;
  const giantAncestryProfile = speciesUseProfile(form, 'goliath-giant-ancestry', speciesProficiency);

  const recordSpeciesUse = async (featureId, callback, featKey) => {
    if (speciesUseInFlight.current) return;
    const id = characterId || form?._id || form?.characterId;
    if (!id || !onCharacterChange) { setError('Character updates are unavailable. Reopen the sheet and try again.'); return; }
    speciesUseInFlight.current = true;
    setSpeciesUseBusy(true);
    setError(null);
    const revision = speciesRevision(form);
    if (!speciesUseRequest.current || speciesUseRequest.current.featureId !== featureId || speciesUseRequest.current.revision !== revision) {
      speciesUseRequest.current = { featureId, revision, eventId: window.crypto?.randomUUID?.() || `species-${Date.now()}-${Math.random().toString(36).slice(2)}` };
    }
    try {
      const response = await apiFetch(`/characters/${encodeURIComponent(id)}/species/uses`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(speciesUseRequest.current),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'The species use could not be saved.');
      if (!Number.isInteger(result.speciesResources?.revision)) throw new Error('The server did not return saved species uses.');
      onCharacterChange(previous => mergeSpeciesResources(previous, result.speciesResources));
      speciesUseRequest.current = null;
      if (!result.replayed) callback?.(result);
      markRecentlyUsed(featKey);
    } catch (error) {
      setError(error.message || 'The species use could not be saved. Retry to confirm its status.');
    } finally {
      speciesUseInFlight.current = false;
      setSpeciesUseBusy(false);
    }
  };

  const { gnomeLineage, gnomeLineageKey, gnomeLineageAbility } = useMemo(() => {
    const race = form?.race || {};
    const lineageFromForm =
      typeof form?.gnomeLineage === 'object' ? form.gnomeLineage : null;
    const lineageKeyFromForm =
      typeof form?.gnomeLineageKey === 'string' ? form.gnomeLineageKey : '';
    const abilityFromForm =
      typeof form?.gnomeLineageAbility === 'string'
        ? form.gnomeLineageAbility
        : '';

    let lineage = lineageFromForm;
    let lineageKey = lineageKeyFromForm;
    if (!lineage) {
      if (lineageKey && race?.gnomeLineages?.[lineageKey]) {
        lineage = race.gnomeLineages[lineageKey];
      } else if (race?.selectedAncestry) {
        lineage = race.selectedAncestry;
        lineageKey =
          typeof race?.selectedAncestryKey === 'string'
            ? race.selectedAncestryKey
            : '';
      } else if (
        typeof race?.selectedAncestryKey === 'string' &&
        race?.gnomeLineages?.[race.selectedAncestryKey]
      ) {
        lineage = race.gnomeLineages[race.selectedAncestryKey];
        lineageKey = race.selectedAncestryKey;
      }
    }

    let ability = abilityFromForm;
    if (!ability) {
      ability =
        typeof race?.selectedLineageAbility === 'string'
          ? race.selectedLineageAbility
          : '';
    }
    if (!ability && lineage?.spellcastingAbilities?.length) {
      ability = lineage.spellcastingAbilities[0];
    }

    return { gnomeLineage: lineage, gnomeLineageKey: lineageKey, gnomeLineageAbility: ability };
  }, [form?.gnomeLineage, form?.gnomeLineageAbility, form?.gnomeLineageKey, form?.race]);

  const gnomeSpellAbilityLabel = useMemo(() => {
    if (typeof gnomeLineageAbility !== 'string') return '';
    return gnomeLineageAbility.trim();
  }, [gnomeLineageAbility]);

  const { tieflingLegacy, tieflingLegacyKey, tieflingLegacyAbility } = useMemo(() => {
    const race = form?.race || {};
    const legacyFromForm =
      typeof form?.tieflingLegacy === 'object' ? form.tieflingLegacy : null;
    const legacyKeyFromForm =
      typeof form?.tieflingLegacyKey === 'string' ? form.tieflingLegacyKey : '';
    const abilityFromForm =
      typeof form?.tieflingLegacyAbility === 'string'
        ? form.tieflingLegacyAbility
        : '';

    let legacy = legacyFromForm;
    let legacyKey = legacyKeyFromForm;
    if (!legacy) {
      if (legacyKey && race?.fiendishLegacies?.[legacyKey]) {
        legacy = race.fiendishLegacies[legacyKey];
      } else if (race?.selectedAncestry && race?.fiendishLegacies) {
        legacy = race.selectedAncestry;
        legacyKey =
          typeof race?.selectedAncestryKey === 'string'
            ? race.selectedAncestryKey
            : '';
      } else if (
        typeof race?.selectedAncestryKey === 'string' &&
        race?.fiendishLegacies?.[race.selectedAncestryKey]
      ) {
        legacy = race.fiendishLegacies[race.selectedAncestryKey];
        legacyKey = race.selectedAncestryKey;
      }
    }

    let ability = abilityFromForm;
    if (!ability) {
      ability =
        typeof race?.selectedLineageAbility === 'string'
          ? race.selectedLineageAbility
          : '';
    }
    if (!ability && legacy?.spellcastingAbilities?.length) {
      ability = legacy.spellcastingAbilities[0];
    }

    return {
      tieflingLegacy: legacy,
      tieflingLegacyKey: legacyKey,
      tieflingLegacyAbility: ability,
    };
  }, [
    form?.race,
    form?.tieflingLegacy,
    form?.tieflingLegacyAbility,
    form?.tieflingLegacyKey,
  ]);

  const tieflingSpellAbilityLabel = useMemo(() => {
    if (typeof tieflingLegacyAbility !== 'string') return '';
    return tieflingLegacyAbility.trim();
  }, [tieflingLegacyAbility]);

  const tieflingResistanceLabel = useMemo(() => {
    const resistance = tieflingLegacy?.resistance;
    if (typeof resistance !== 'string') {
      return '';
    }
    return resistance.trim();
  }, [tieflingLegacy]);

  const { elvenLineage, elvenLineageKey, elvenLineageAbility } = useMemo(() => {
    const race = form?.race || {};
    const lineageFromForm =
      typeof form?.elvenLineage === 'object' ? form.elvenLineage : null;
    const lineageKeyFromForm =
      typeof form?.elvenLineageKey === 'string' ? form.elvenLineageKey : '';
    const abilityFromForm =
      typeof form?.elvenLineageAbility === 'string' ? form.elvenLineageAbility : '';

    let lineage = lineageFromForm;
    let lineageKey = lineageKeyFromForm;
    if (!lineage) {
      if (lineageKey && race?.elvenLineages?.[lineageKey]) {
        lineage = race.elvenLineages[lineageKey];
      } else if (race?.selectedAncestry && race?.elvenLineages) {
        lineage = race.selectedAncestry;
        lineageKey =
          typeof race?.selectedAncestryKey === 'string'
            ? race.selectedAncestryKey
            : '';
      } else if (
        typeof race?.selectedAncestryKey === 'string' &&
        race?.elvenLineages?.[race.selectedAncestryKey]
      ) {
        lineage = race.elvenLineages[race.selectedAncestryKey];
        lineageKey = race.selectedAncestryKey;
      }
    }

    let ability = abilityFromForm;
    if (!ability) {
      ability =
        typeof race?.selectedLineageAbility === 'string'
          ? race.selectedLineageAbility
          : '';
    }
    if (!ability && lineage?.spellcastingAbilities?.length) {
      ability = lineage.spellcastingAbilities[0];
    }

    return { elvenLineage: lineage, elvenLineageKey: lineageKey, elvenLineageAbility: ability };
  }, [form?.elvenLineage, form?.elvenLineageAbility, form?.elvenLineageKey, form?.race]);

  const elvenSpellAbilityLabel = useMemo(() => {
    if (typeof elvenLineageAbility !== 'string') return '';
    return elvenLineageAbility.trim();
  }, [elvenLineageAbility]);

  const isDrowElvenLineage = useMemo(() => {
    if (!elvenLineageKey) return false;
    return elvenLineageKey.toLowerCase() === 'drow';
  }, [elvenLineageKey]);

  const isHighElvenLineage = useMemo(() => {
    if (!elvenLineageKey) return false;
    return elvenLineageKey.toLowerCase() === 'high';
  }, [elvenLineageKey]);

  const isWoodElvenLineage = useMemo(() => {
    if (!elvenLineageKey) return false;
    return elvenLineageKey.toLowerCase() === 'wood';
  }, [elvenLineageKey]);

  const isForestGnomeLineage = useMemo(() => {
    if (!gnomeLineageKey) return false;
    return gnomeLineageKey.toLowerCase() === 'forest';
  }, [gnomeLineageKey]);

  const isRockGnomeLineage = useMemo(() => {
    if (!gnomeLineageKey) return false;
    return gnomeLineageKey.toLowerCase() === 'rock';
  }, [gnomeLineageKey]);

  const normalizedTieflingLegacyKey = useMemo(() => {
    if (!tieflingLegacyKey) return '';
    return tieflingLegacyKey.toLowerCase();
  }, [tieflingLegacyKey]);

  const isAbyssalTieflingLegacy = useMemo(() => {
    return normalizedTieflingLegacyKey === 'abyssal';
  }, [normalizedTieflingLegacyKey]);

  const isChthonicTieflingLegacy = useMemo(() => {
    return normalizedTieflingLegacyKey === 'chthonic';
  }, [normalizedTieflingLegacyKey]);

  const isInfernalTieflingLegacy = useMemo(() => {
    return normalizedTieflingLegacyKey === 'infernal';
  }, [normalizedTieflingLegacyKey]);

  const canUseSpeakWithAnimals = useMemo(() => {
    return isForestGnomeLineage;
  }, [isForestGnomeLineage]);

  const speakWithAnimalsMaxUses = useMemo(() => {
    if (!canUseSpeakWithAnimals) return 0;
    return Number.isFinite(profBonus) && profBonus > 0
      ? Math.floor(profBonus)
      : 0;
  }, [canUseSpeakWithAnimals, profBonus]);

  const hasAvailableSlotOfLevel = useMemo(() => {
    const normalizedRegular = availableSlots?.regular || {};
    const normalizedWarlock = availableSlots?.warlock || {};

    return (minimumLevel = 1) => {
      const hasRegularSlot = Object.entries(normalizedRegular).some(
        ([level, count]) =>
          Number(level) >= minimumLevel && Number(count) > 0
      );

      if (hasRegularSlot) {
        return true;
      }

      return Object.entries(normalizedWarlock).some(
        ([level, count]) =>
          Number(level) >= minimumLevel && Number(count) > 0
      );
    };
  }, [availableSlots]);

  const speakWithAnimalsHasSlot = useMemo(
    () => hasAvailableSlotOfLevel(1),
    [hasAvailableSlotOfLevel]
  );

  const lineageSpellEligibility = useMemo(() => {
    return {
      'elf-drow-faerie-fire': isDrowElvenLineage && totalCharacterLevel >= 3,
      'elf-drow-darkness': isDrowElvenLineage && totalCharacterLevel >= 5,
      'elf-high-detect-magic': isHighElvenLineage && totalCharacterLevel >= 3,
      'elf-high-misty-step': isHighElvenLineage && totalCharacterLevel >= 5,
      'elf-wood-longstrider': isWoodElvenLineage && totalCharacterLevel >= 3,
      'elf-wood-pass-without-trace':
        isWoodElvenLineage && totalCharacterLevel >= 5,
      'tiefling-abyssal-ray-of-sickness':
        isAbyssalTieflingLegacy && totalCharacterLevel >= 3,
      'tiefling-abyssal-hold-person':
        isAbyssalTieflingLegacy && totalCharacterLevel >= 5,
      'tiefling-chthonic-false-life':
        isChthonicTieflingLegacy && totalCharacterLevel >= 3,
      'tiefling-chthonic-ray-of-enfeeblement':
        isChthonicTieflingLegacy && totalCharacterLevel >= 5,
      'tiefling-infernal-hellish-rebuke':
        isInfernalTieflingLegacy && totalCharacterLevel >= 3,
      'tiefling-infernal-darkness':
        isInfernalTieflingLegacy && totalCharacterLevel >= 5,
    };
  }, [
    isDrowElvenLineage,
    isHighElvenLineage,
    isWoodElvenLineage,
    isAbyssalTieflingLegacy,
    isChthonicTieflingLegacy,
    isInfernalTieflingLegacy,
    totalCharacterLevel,
  ]);

  const speakWithAnimalsUses = canUseSpeakWithAnimals ? Math.max(0, speakWithAnimalsMaxUses -
    (Number(form.spellcastingState?.speciesFreeCasts?.['gnome-forest-speak-with-animals']) || 0)) : 0;
  const lineageSpellUses = useMemo(() => Object.fromEntries(LIMITED_USE_LINEAGE_SPELL_IDS.map(id => [id,
    lineageSpellEligibility[id] ? Math.max(0, (LINEAGE_SPELLS[id]?.maxUses || 0) - (Number(form.spellcastingState?.speciesFreeCasts?.[id]) || 0)) : 0,
  ])), [form.spellcastingState, lineageSpellEligibility]);
  speakWithAnimalsUsesRef.current = speakWithAnimalsUses;
  lineageSpellUsesRef.current = lineageSpellUses;

  const ancestryFeatures = useMemo(() => {
    const race = form?.race;
    if (!race) return [];

    const raceName =
      typeof race?.name === 'string' ? race.name.toLowerCase() : '';
    const raceDisplayName =
      typeof race?.name === 'string' && race.name.trim()
        ? race.name.trim()
        : raceName
        ? raceName.charAt(0).toUpperCase() + raceName.slice(1)
        : 'Race';

    const darkvisionRange =
      Number.isFinite(race?.darkvisionRange) && race.darkvisionRange > 0
        ? race.darkvisionRange
        : raceName === 'dwarf'
        ? 120
        : null;

    const raceFeatures = [];

    if (raceName === 'dwarf') {
      const darkvisionDescription =
        `Accustomed to life underground, you can see in dim light within ${darkvisionRange ?? 60} ` +
        'feet of you as if it were bright light, and in darkness as if it were dim light. You cannot discern color in darkness, only shades of gray.';

      const resilienceDescription =
        'You have resistance to poison damage, and you have advantage on saving throws you make to avoid or end the Poisoned condition.';

      const toughnessDescription =
        'Your hit point maximum increases by 1, and it increases by 1 again whenever you gain a level.';

      const stonecunningUsage = 'Bonus action • Proficiency bonus per long rest';
      const stonecunningDescription =
        'As a bonus action, you gain tremorsense with a range of 60 feet for 10 minutes. You can use this bonus action a number of times equal to your proficiency bonus, and you regain all expended uses when you finish a long rest.';
      const stonecunningFullDescription = `${stonecunningDescription} ${stonecunningUsage}`;

      raceFeatures.push(
        {
          id: 'dwarf-darkvision',
          name: 'Darkvision',
          meta: `${raceDisplayName} ${darkvisionRange ?? 60} ft`,
          description: darkvisionDescription,
          desc: darkvisionDescription,
          hideUseButton: true,
        },
        {
          id: 'dwarf-resilience',
          name: 'Dwarven Resilience',
          meta: 'Dwarf',
          description: resilienceDescription,
          desc: resilienceDescription,
          hideUseButton: true,
        },
        {
          id: 'dwarf-toughness',
          name: 'Dwarven Toughness',
          meta: 'Dwarf',
          description: toughnessDescription,
          desc: toughnessDescription,
          hideUseButton: true,
        },
        {
          id: 'dwarf-stonecunning',
          name: 'Stonecunning',
          meta: 'Dwarf',
          description: stonecunningFullDescription,
          desc: stonecunningFullDescription,
          hideUseButton: true,
        }
      );
    } else if (raceName === 'orc') {
      const orcDarkvisionRange =
        Number.isFinite(race?.darkvisionRange) && race.darkvisionRange > 0
          ? race.darkvisionRange
          : 120;
      const darkvisionDescription =
        `Thanks to your orc heritage, you can see in dim light within ${orcDarkvisionRange} feet of you as if it were bright light, and in darkness as if it were dim light. You cannot discern color in darkness, only shades of gray.`;
      const adrenalineRushDescription =
        'As a bonus action, you can take the Dash action and gain temporary hit points equal to your proficiency bonus. You can use this trait a number of times equal to your proficiency bonus, and you regain all expended uses when you finish a Short or Long Rest.';
      const relentlessEnduranceDescription =
        "When you are reduced to 0 Hit Points but not killed outright, you can drop to 1 Hit Point instead. Once you use this trait, you can't do so again until you finish a Long Rest. Applied automatically when damage would drop you to 0.";

      raceFeatures.push(
        {
          id: 'orc-adrenaline-rush',
          name: 'Adrenaline Rush',
          meta: 'Orc',
          description: adrenalineRushDescription,
          desc: adrenalineRushDescription,
        },
        {
          id: 'orc-darkvision',
          name: 'Darkvision',
          meta: `${raceDisplayName} ${orcDarkvisionRange} ft`,
          description: darkvisionDescription,
          desc: darkvisionDescription,
          hideUseButton: true,
        },
        {
          id: 'orc-relentless-endurance',
          name: 'Relentless Endurance',
          meta: `Orc · ${relentlessEnduranceUsed ? 'Used until a Long Rest' : 'Ready'}`,
          description: relentlessEnduranceDescription,
          desc: relentlessEnduranceDescription,
          hideUseButton: true,
        }
      );
    } else if (raceName === 'elf') {
      const feyAncestryDescription =
        'You have Advantage on saving throws you make to avoid or end the Charmed condition.';
      const tranceDescription =
        'You don’t need to sleep, and magic can’t put you to sleep. You can finish a Long Rest in 4 hours if you spend those hours in a trancelike meditation, during which you stay conscious.';
      const keenSensesDescription =
        'You have proficiency in the Insight, Perception or Survival skill (your choice).';

      raceFeatures.push(
        {
          id: 'elf-fey-ancestry',
          name: 'Fey Ancestry',
          meta: 'Elf',
          description: feyAncestryDescription,
          desc: feyAncestryDescription,
          hideUseButton: true,
        },
        {
          id: 'elf-trance',
          name: 'Trance',
          meta: 'Elf',
          description: tranceDescription,
          desc: tranceDescription,
          hideUseButton: true,
        },
        {
          id: 'elf-keen-senses',
          name: 'Keen Senses',
          meta: 'Elf',
          description: keenSensesDescription,
          desc: keenSensesDescription,
          hideUseButton: true,
        }
      );

      if (elvenLineage) {
        const lineageLabel =
          typeof elvenLineage?.label === 'string'
            ? elvenLineage.label
            : 'Elven Lineage';
        const lineageMeta = `${lineageLabel}${
          elvenSpellAbilityLabel
            ? ` • Spellcasting Ability: ${elvenSpellAbilityLabel}`
            : ''
        }`;
        const abilityText = elvenSpellAbilityLabel
          ? ` This lineage uses ${elvenSpellAbilityLabel} for its spells.`
          : '';

        if (isDrowElvenLineage) {
          const dancingLightsDescription =
            'You know the Dancing Lights cantrip and can cast it without expending a spell slot.' +
            abilityText;
          const faerieFireDescription =
            'Starting at 3rd level, you can cast Faerie Fire with this trait once per long rest.' +
            abilityText;
          const darknessDescription =
            'Starting at 5th level, you can cast Darkness with this trait once per long rest.' +
            abilityText;

          raceFeatures.push({
            id: 'elf-drow-dancing-lights',
            name: 'Dancing Lights',
            meta: lineageMeta,
            description: dancingLightsDescription,
            desc: dancingLightsDescription,
            hideUseButton: true,
          });

          if (totalCharacterLevel >= 3) {
            raceFeatures.push({
              id: 'elf-drow-faerie-fire',
              name: 'Faerie Fire (Level 3)',
              meta: lineageMeta,
              description: faerieFireDescription,
              desc: faerieFireDescription,
              oncePerLongRestLineageSpell: true,
              lineageSpellName: LINEAGE_SPELLS['elf-drow-faerie-fire']?.spellName,
            });
          }

          if (totalCharacterLevel >= 5) {
            raceFeatures.push({
              id: 'elf-drow-darkness',
              name: 'Darkness (Level 5)',
              meta: lineageMeta,
              description: darknessDescription,
              desc: darknessDescription,
              oncePerLongRestLineageSpell: true,
              lineageSpellName: LINEAGE_SPELLS['elf-drow-darkness']?.spellName,
            });
          }
        } else if (isHighElvenLineage) {
          const prestidigitationDescription =
            'You know the Prestidigitation cantrip and can cast it without expending a spell slot.' +
            abilityText;
          const detectMagicDescription =
            'Starting at 3rd level, you can cast Detect Magic with this trait once per long rest.' +
            abilityText;
          const mistyStepDescription =
            'Starting at 5th level, you can cast Misty Step with this trait once per long rest.' +
            abilityText;

          raceFeatures.push({
            id: 'elf-high-prestidigitation',
            name: 'Prestidigitation',
            meta: lineageMeta,
            description: prestidigitationDescription,
            desc: prestidigitationDescription,
            hideUseButton: true,
          });

          if (totalCharacterLevel >= 3) {
            raceFeatures.push({
              id: 'elf-high-detect-magic',
              name: 'Detect Magic (Level 3)',
              meta: lineageMeta,
              description: detectMagicDescription,
              desc: detectMagicDescription,
              oncePerLongRestLineageSpell: true,
              lineageSpellName: LINEAGE_SPELLS['elf-high-detect-magic']?.spellName,
            });
          }

          if (totalCharacterLevel >= 5) {
            raceFeatures.push({
              id: 'elf-high-misty-step',
              name: 'Misty Step (Level 5)',
              meta: lineageMeta,
              description: mistyStepDescription,
              desc: mistyStepDescription,
              oncePerLongRestLineageSpell: true,
              lineageSpellName: LINEAGE_SPELLS['elf-high-misty-step']?.spellName,
            });
          }
        } else if (isWoodElvenLineage) {
          const druidcraftDescription =
            'You know the Druidcraft cantrip and can cast it without expending a spell slot.' +
            abilityText +
            ' Your walking speed increases to 35 feet.';
          const longstriderDescription =
            'Starting at 3rd level, you can cast Longstrider with this trait once per long rest.' +
            abilityText;
          const passWithoutTraceDescription =
            'Starting at 5th level, you can cast Pass without Trace with this trait once per long rest.' +
            abilityText;

          raceFeatures.push({
            id: 'elf-wood-druidcraft',
            name: 'Druidcraft',
            meta: lineageMeta,
            description: druidcraftDescription,
            desc: druidcraftDescription,
            hideUseButton: true,
          });

          if (totalCharacterLevel >= 3) {
            raceFeatures.push({
              id: 'elf-wood-longstrider',
              name: 'Longstrider (Level 3)',
              meta: lineageMeta,
              description: longstriderDescription,
              desc: longstriderDescription,
              oncePerLongRestLineageSpell: true,
              lineageSpellName: LINEAGE_SPELLS['elf-wood-longstrider']?.spellName,
            });
          }

          if (totalCharacterLevel >= 5) {
            raceFeatures.push({
              id: 'elf-wood-pass-without-trace',
              name: 'Pass without Trace (Level 5)',
              meta: lineageMeta,
              description: passWithoutTraceDescription,
              desc: passWithoutTraceDescription,
              oncePerLongRestLineageSpell: true,
              lineageSpellName:
                LINEAGE_SPELLS['elf-wood-pass-without-trace']?.spellName,
            });
          }
        }
      }
    } else if (raceName === 'gnome') {
      const gnomishCunningDescription =
        'You have Advantage on Intelligence, Wisdom, and Charisma saving throws. (2014 limited this to saves against magic.)';

      raceFeatures.push({
        id: 'gnome-gnomish-cunning',
        name: 'Gnomish Cunning',
        meta: 'Gnome',
        description: gnomishCunningDescription,
        desc: gnomishCunningDescription,
        hideUseButton: true,
      });

      const lineageLabel =
        typeof gnomeLineage?.label === 'string'
          ? gnomeLineage.label
          : 'Gnome Lineage';
      const abilityText = gnomeSpellAbilityLabel
        ? ` This lineage uses ${gnomeSpellAbilityLabel} for its spells.`
        : '';

      if (gnomeLineage && isForestGnomeLineage) {
        const minorIllusionDescription =
          'You know the Minor Illusion cantrip. It creates a sound or an image of an object within range that lasts for the duration.' +
          abilityText;

        raceFeatures.push({
          id: 'gnome-forest-minor-illusion',
          name: 'Minor Illusion',
          meta: `${lineageLabel}${
            gnomeSpellAbilityLabel ? ` • Spellcasting Ability: ${gnomeSpellAbilityLabel}` : ''
          }`,
          description: minorIllusionDescription,
          desc: minorIllusionDescription,
          hideUseButton: true,
        });

        const speakWithAnimalsDescription =
          'From level 1, you always have Speak with Animals prepared. You can cast it without expending a spell slot a number of times equal to your proficiency bonus. You regain all expended uses when you finish a Long Rest. You can also cast it using spell slots.' +
          abilityText;

        raceFeatures.push({
          id: 'gnome-forest-speak-with-animals',
          name: 'Speak with Animals',
          meta: `${lineageLabel}${
            gnomeSpellAbilityLabel ? ` • Spellcasting Ability: ${gnomeSpellAbilityLabel}` : ''
          }`,
          description: speakWithAnimalsDescription,
          desc: speakWithAnimalsDescription,
        });
      } else if (gnomeLineage && isRockGnomeLineage) {
        const mendingDescription =
          'You know the Mending cantrip, allowing you to repair small breaks or tears in objects.' +
          abilityText;

        raceFeatures.push({
          id: 'gnome-rock-mending',
          name: 'Mending',
          meta: `${lineageLabel}${
            gnomeSpellAbilityLabel ? ` • Spellcasting Ability: ${gnomeSpellAbilityLabel}` : ''
          }`,
          description: mendingDescription,
          desc: mendingDescription,
          hideUseButton: true,
        });

        const prestidigitationDescription =
          'You know the Prestidigitation cantrip, letting you create minor magical effects.' +
          abilityText +
          ' Additionally, whenever you finish a long rest, you can spend 10 minutes to create a Tiny clockwork device (AC 5, 1 hp). The device ceases to function after 24 hours (unless you spend 1 minute repairing it), when you use this trait again, or when you take an action to dismantle it; at that time, you can reclaim the materials used to create it.';

        raceFeatures.push({
          id: 'gnome-rock-prestidigitation',
          name: 'Prestidigitation',
          meta: `${lineageLabel}${
            gnomeSpellAbilityLabel ? ` • Spellcasting Ability: ${gnomeSpellAbilityLabel}` : ''
          }`,
          description: prestidigitationDescription,
          desc: prestidigitationDescription,
          hideUseButton: true,
        });
      }
    } else if (raceName === 'tiefling') {
      const legacyLabel =
        typeof tieflingLegacy?.label === 'string'
          ? tieflingLegacy.label
          : typeof tieflingLegacy?.name === 'string'
          ? tieflingLegacy.name
          : 'Fiendish Legacy';
      const legacyMetaParts = [legacyLabel];
      if (tieflingResistanceLabel) {
        legacyMetaParts.push(`Resistance: ${tieflingResistanceLabel}`);
      }
      if (tieflingSpellAbilityLabel) {
        legacyMetaParts.push(`Spellcasting Ability: ${tieflingSpellAbilityLabel}`);
      }
      const legacyMeta = legacyMetaParts.join(' • ');
      const legacySpellMeta = [
        legacyLabel,
        tieflingSpellAbilityLabel
          ? `Spellcasting Ability: ${tieflingSpellAbilityLabel}`
          : null,
      ]
        .filter(Boolean)
        .join(' • ');
      const abilityText = tieflingSpellAbilityLabel
        ? ` This legacy uses ${tieflingSpellAbilityLabel} for its spells.`
        : '';

      const baseTieflingMeta = [
        'Tiefling',
        tieflingSpellAbilityLabel ? `Spellcasting Ability: ${tieflingSpellAbilityLabel}` : null,
      ]
        .filter(Boolean)
        .join(' • ');

      const otherworldlyDescription =
        'You know the Thaumaturgy cantrip and can cast it without expending a spell slot.' +
        abilityText;

      raceFeatures.push({
        id: 'tiefling-otherworldly-presence',
        name: 'Otherworldly Presence (Thaumaturgy)',
        meta: baseTieflingMeta,
        description: otherworldlyDescription,
        desc: otherworldlyDescription,
        hideUseButton: true,
      });

      if (tieflingResistanceLabel) {
        const resistanceDescription =
          `You have resistance to ${tieflingResistanceLabel.toLowerCase()} damage.`;
        raceFeatures.push({
          id: `tiefling-${normalizedTieflingLegacyKey || 'legacy'}-resistance`,
          name: `${tieflingResistanceLabel} Resistance`,
          meta: legacyMeta,
          description: resistanceDescription,
          desc: resistanceDescription,
          hideUseButton: true,
        });
      }

      const spells = Array.isArray(tieflingLegacy?.spells)
        ? tieflingLegacy.spells
        : [];

      const legacyKeyForId = normalizedTieflingLegacyKey || 'legacy';

      spells.forEach((spell, index) => {
        const requiredLevelRaw = Number(spell?.unlockedAtLevel);
        const requiredLevel = Number.isFinite(requiredLevelRaw)
          ? requiredLevelRaw
          : 1;
        if (totalCharacterLevel < Math.max(1, requiredLevel)) {
          return;
        }

        const spellName = typeof spell?.name === 'string' ? spell.name : 'Legacy Spell';
        const normalizedSpellName = spellName
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '') || `spell-${index}`;
        const spellId = `tiefling-${legacyKeyForId}-${normalizedSpellName}`;
        const baseSpellConfig = LINEAGE_SPELLS[spellId];
        const hasLimitedUses =
          Number.isFinite(baseSpellConfig?.maxUses) && baseSpellConfig.maxUses > 0;

        const descriptionParts = [];
        if (typeof spell?.description === 'string' && spell.description.trim()) {
          descriptionParts.push(spell.description.trim());
        }
        if (abilityText) {
          descriptionParts.push(abilityText.trim());
        }
        if (typeof spell?.usage === 'string' && spell.usage.trim()) {
          descriptionParts.push(`Uses: ${spell.usage.trim()}`);
        }
        const description = descriptionParts.join(' ');
        const levelNote = requiredLevel > 1 ? ` (Level ${requiredLevel})` : '';

        raceFeatures.push({
          id: spellId,
          name: `${spellName}${levelNote}`,
          meta: legacySpellMeta,
          description,
          desc: description,
          hideUseButton: !hasLimitedUses,
          oncePerLongRestLineageSpell: hasLimitedUses,
          lineageSpellName: baseSpellConfig?.spellName || spellName,
          lineageSpellBaseLevel: baseSpellConfig?.baseLevel,
          lineageSpellCastingTime: baseSpellConfig?.castingTime,
        });
      });
    }

    if (darkvisionRange && raceName !== 'dwarf' && raceName !== 'orc') {
      const darkvisionDescription =
        `You can see in dim light within ${darkvisionRange} ` +
        'feet of you as if it were bright light, and in darkness as if it were dim light. You cannot discern color in darkness, only shades of gray.';

      raceFeatures.push({
        id: raceName ? `${raceName}-darkvision` : 'darkvision',
        name: 'Darkvision',
        meta: `${raceDisplayName} ${darkvisionRange ?? 60} ft`,
        description: darkvisionDescription,
        desc: darkvisionDescription,
        hideUseButton: true,
      });
    }

    if (raceName === 'dragonborn') {
      const ancestry =
        race.selectedAncestry ||
        (race.selectedAncestryKey && race.dragonAncestries
          ? race.dragonAncestries[race.selectedAncestryKey]
          : null) ||
        form?.dragonAncestry ||
        (form?.dragonAncestryKey && race.dragonAncestries
          ? race.dragonAncestries[form.dragonAncestryKey]
          : null);

      if (ancestry) {
        const ancestryLabel = ancestry.label || ancestry.name || 'Dragonborn';
        const damageType = ancestry.damageType || '';
        const damageTypeLower = damageType.toLowerCase();
        const resistanceDescription = damageTypeLower
          ? `You have resistance to ${damageTypeLower} damage.`
          : 'You have resistance to the damage type associated with your draconic ancestry.';

        raceFeatures.push({
          id: 'dragonborn-damage-resistance',
          name: 'Damage Resistance',
          meta: `Dragon Subrace (${ancestryLabel})`,
          description: resistanceDescription,
          desc: resistanceDescription,
          hideUseButton: true,
        });

        if (totalCharacterLevel >= 5) {
          const draconicFlightDescription =
            'From character level 5, you can take a Bonus Action to sprout spectral wings that last 10 minutes, until you retract them (no action required) or until you have the Incapacitated condition. During that time you have a Fly Speed equal to your Speed. Once per Long Rest.';
          raceFeatures.push({
            id: 'dragonborn-draconic-flight',
            name: 'Draconic Flight',
            meta: `Dragon Subrace (${ancestryLabel})`,
            description: draconicFlightDescription,
            desc: draconicFlightDescription,
            hideUseButton: true,
          });
        }
      }
    }

    if (raceName === 'goliath') {
      const ancestry =
        race.selectedAncestry ||
        (race.selectedAncestryKey && race.giantAncestries
          ? race.giantAncestries[race.selectedAncestryKey]
          : null) ||
        form?.giantAncestry ||
        (form?.giantAncestryKey && race.giantAncestries
          ? race.giantAncestries[form.giantAncestryKey]
          : null);

      {
        const ancestryLabel = ancestry ? ancestry.label || ancestry.name || 'Giant Boon' : 'Giant Ancestry (not chosen)';
        const ancestryDescription = ancestry ? ancestry.description || ''
          : 'No Giant Ancestry is recorded for this character. Ask the DM to set it (Cloud, Fire, Frost, Hill, Stone or Storm).';
        const usageText = ancestry?.usage ? ` ${ancestry.usage}` : '';
        const combinedDescription = `${ancestryDescription}${usageText}`.trim();

        raceFeatures.push(
          {
            id: `goliath-ancestry-${
              race.selectedAncestryKey || form?.giantAncestryKey || 'boon'
            }`,
            name: ancestryLabel,
            meta: ancestry ? `Giant Ancestry · ${giantAncestryProfile.remaining}/${giantAncestryProfile.maximum} uses` : 'Giant Ancestry',
            description: combinedDescription || ancestryDescription,
            desc: combinedDescription || ancestryDescription,
            hideUseButton: true,
          },
          {
            id: 'goliath-powerful-build',
            name: 'Powerful Build',
            meta: 'Goliath',
            description:
              'You have Advantage on any ability check you make to end the Grappled condition, and you count as one size larger when determining your carrying capacity.',
            desc:
              'You have Advantage on any ability check you make to end the Grappled condition, and you count as one size larger when determining your carrying capacity.',
            hideUseButton: true,
          }
        );

        if (totalCharacterLevel >= 5) {
          const largeFormDescription =
            "Starting at 5th level, you can use a bonus action to magically grow to Large size for 10 minutes. While Large, your speed increases by 10 feet, and you have advantage on Strength checks. Once you use this trait, you can't use it again until you finish a long rest.";
          raceFeatures.push({
            id: 'goliath-large-form',
            name: 'Large Form',
            meta: 'Goliath (Level 5)',
            description: largeFormDescription,
            desc: largeFormDescription,
            hideUseButton: true,
          });
        }
      }
    } else if (raceName === 'human') {
      // 2024 Human traits (SRD 5.2). Older snapshots without them still list the three traits.
      const humanTraits = Array.isArray(race?.traits) && race.traits.length ? race.traits : [
        { name: 'Resourceful', description: 'You gain Heroic Inspiration whenever you finish a Long Rest.' },
        { name: 'Skillful', description: 'You gain proficiency in one skill of your choice.' },
        { name: 'Versatile', description: 'You gain an Origin feat of your choice. Skilled is recommended.' },
      ];
      raceFeatures.push(...humanTraits.map((trait) => ({
        id: `human-${String(trait.name).toLowerCase()}`,
        name: trait.name,
        meta: 'Human',
        description: trait.description,
        desc: trait.description,
        hideUseButton: true,
      })));
    } else if (raceName === 'halfling') {
      raceFeatures.push(
        {
          id: 'halfling-brave',
          name: 'Brave',
          meta: 'Halfling',
          description:
            'You have advantage on saving throws you make to avoid or end the Frightened condition.',
          desc:
            'You have advantage on saving throws you make to avoid or end the Frightened condition.',
          hideUseButton: true,
        },
        {
          id: 'halfling-nimbleness',
          name: 'Halfling Nimbleness',
          meta: 'Halfling',
          description:
            'You can move through the space of any creature that is of a size larger than yours.',
          desc:
            'You can move through the space of any creature that is of a size larger than yours.',
          hideUseButton: true,
        },
        {
          id: 'halfling-luck',
          name: 'Luck',
          meta: 'Halfling',
          description:
            'When you roll a 1 on the d20 of a D20 Test, you can reroll the die, and you must use the new roll. Rerolled automatically on your attack rolls, ability checks, saving throws (death saves too) and Initiative.',
          desc:
            'When you roll a 1 on the d20 of a D20 Test, you can reroll the die, and you must use the new roll. Rerolled automatically on your attack rolls, ability checks, saving throws (death saves too) and Initiative.',
          hideUseButton: true,
        },
        {
          id: 'halfling-naturally-stealthy',
          name: 'Naturally Stealthy',
          meta: 'Halfling',
          description:
            'You can attempt to hide even when you are obscured only by a creature that is at least one size larger than you.',
          desc:
            'You can attempt to hide even when you are obscured only by a creature that is at least one size larger than you.',
          hideUseButton: true,
        }
      );
    }

    return raceFeatures;
  }, [
    form?.race,
    form?.dragonAncestry,
    relentlessEnduranceUsed,
    giantAncestryProfile.remaining,
    giantAncestryProfile.maximum,
    form?.dragonAncestryKey,
    form?.giantAncestry,
    form?.giantAncestryKey,
    totalCharacterLevel,
    gnomeLineage,
    gnomeSpellAbilityLabel,
    isForestGnomeLineage,
    isRockGnomeLineage,
    elvenLineage,
    elvenLineageKey,
    elvenSpellAbilityLabel,
    isDrowElvenLineage,
    isHighElvenLineage,
    isWoodElvenLineage,
    tieflingLegacy,
    tieflingLegacyKey,
    tieflingSpellAbilityLabel,
    tieflingResistanceLabel,
    normalizedTieflingLegacyKey,
  ]);

  const classFeatures = useMemo(() => {
    // Hidden placeholder rows (e.g. the server's "Barbarian Subclass") must not replace the sheet's own rows.
    const existingKeys = new Set(
      features.filter(isClassFeatureDisplayable).map((feature) =>
        `${String(feature?.class || '').toLowerCase()}:${String(feature?.name || '').toLowerCase()}`
      )
    );
    const generatedBarbarianFeatures = barbarianLevel < 1 ? [] : getAvailableBarbarianFeatures(form)
      .filter((feature) => !existingKeys.has('barbarian:' + String(feature.name || '').toLowerCase()))
      .map((feature) => ({
        ...feature,
        hideUseButton: feature.type === 'passive' || feature.id === 'barbarian-weapon-mastery',
        isWeaponMasteryConfig: feature.id === 'barbarian-weapon-mastery',
        isRecklessAttackToggle: feature.id === 'reckless-attack',
      }));
    const visibleFeatures = features.map((feature) => {
      const sourceId = String(feature?.class || '').trim().toLowerCase();
      const isMastery = String(feature?.name || '').trim().toLowerCase() === 'weapon mastery' &&
        masteryEntitlements.some((entitlement) => entitlement.sourceId === sourceId);
      if (FIGHTING_STYLE_CLASSES[sourceId] && feature.name === 'Fighting Style') return { ...feature, hideUseButton: true, isFightingStyleConfig: true, fightingStyleClass: sourceId };
      if (sourceId === 'cleric' && feature.name === 'Divine Order') return { ...feature, hideUseButton: true, isDivineOrderConfig: true };
      if (sourceId === 'cleric' && feature.name === 'Blessed Strikes') return { ...feature, hideUseButton: true, isBlessedStrikesConfig: true };
      if (sourceId === 'druid' && feature.name === 'Primal Order') return { ...feature, hideUseButton: true, isPrimalOrderConfig: true };
      if (sourceId === 'druid' && feature.name === 'Elemental Fury') return { ...feature, hideUseButton: true, isElementalFuryConfig: true };
      if (sourceId === 'druid' && feature.name === 'Wild Shape') return { ...feature, hideUseButton: true, isKnownFormsConfig: true };
      if (sourceId === 'wizard' && feature.name === 'Spellcasting') return { ...feature, hideUseButton: true, isSpellbookConfig: true };
      if (sourceId === 'wizard' && feature.name === 'Spell Mastery') return { ...feature, hideUseButton: true, isSpellMasteryConfig: true };
      if (sourceId === 'wizard' && feature.name === 'Signature Spells') return { ...feature, hideUseButton: true, isSignatureSpellsConfig: true };
      return isMastery
        ? { ...feature, hideUseButton: true, isWeaponMasteryConfig: true, masterySource: sourceId }
        : feature;
    });
    const rogueMastery = getClassLevel(form, 'rogue') > 0 &&
      !existingKeys.has('rogue:weapon mastery')
      ? [{ id: 'rogue-weapon-mastery', name: 'Weapon Mastery', class: 'Rogue', level: 1,
          description: 'Choose two eligible weapon kinds.', hideUseButton: true,
          isWeaponMasteryConfig: true, masterySource: 'rogue' }]
      : [];
    const masteryAwareBarbarian = generatedBarbarianFeatures.map((feature) =>
      feature.isWeaponMasteryConfig ? { ...feature, masterySource: 'barbarian' } : feature
    );
    const subclassFeatures = getSubclassFeatureEntitlements(form).filter(feature => feature.source.classId === 'rogue').map(feature => ({ ...feature, class: 'Rogue', subclass: 'Thief', hideUseButton: true, description: feature.description + (feature.level === 3 && feature.name === 'Fast Hands' ? ' Use Fast Hands on the action bar; the DM adjudicates the subject and item effects.' : feature.presentation?.automation === 'unavailable' ? ' Automation is not yet implemented for this higher-level feature.' : '') }));
    const monkSubclassFeatures = getSubclassFeatureEntitlements(form).filter(feature => feature.source.classId === 'monk')
      .map(feature => ({...feature, class:'Monk', subclass:'Warrior of the Open Hand', hideUseButton:true}));
    const fighterSubclassFeatures = getSubclassFeatureEntitlements(form).filter(feature => feature.source.classId === 'fighter')
      .map(feature => ({...feature, class:'Fighter', subclass:'Champion', hideUseButton:true,
        ...(feature.id === ADDITIONAL_FIGHTING_STYLE_FEATURE_ID ? { isFightingStyleConfig: true, fightingStyleClass: 'fighter', fightingStyleSlot: 'additional' } : {})}));
    const rangerSubclassFeatures = getSubclassFeatureEntitlements(form).filter(feature => feature.source.classId === 'ranger')
      .map(feature => ({...feature, class:'Ranger', subclass:'Hunter', hideUseButton:true, ...(feature.id === HUNTERS_PREY_FEATURE_ID ? { isHuntersPreyConfig: true } : {}), ...(feature.id === DEFENSIVE_TACTICS_FEATURE_ID ? { isDefensiveTacticsConfig: true } : {})}));
    const paladinSubclassFeatures = getSubclassFeatureEntitlements(form).filter(feature => feature.source.classId === 'paladin')
      .map(feature => ({...feature, class:'Paladin', subclass:'Oath of Devotion', hideUseButton:true,
        ...(feature.id === 'paladin:devotion:sacred-weapon' ? { description: `${feature.description} Use it from the Channel Divinity tile on the action bar.` } : {})}));
    const clericSubclassFeatures = getSubclassFeatureEntitlements(form).filter(feature => feature.source.classId === 'cleric')
      .map(feature => ({...feature, class:'Cleric', subclass:'Life Domain', hideUseButton:true,
        ...(feature.id === 'cleric:life:preserve-life' ? { description: `${feature.description} Use it from the Channel Divinity tile on the action bar.` } : {})}));
    const wizardSubclassFeatures = getSubclassFeatureEntitlements(form).filter(feature => feature.source.classId === 'wizard')
      .map(feature => ({...feature, class:'Wizard', subclass:'Evoker', hideUseButton:true}));
    const bardSubclassFeatures = getSubclassFeatureEntitlements(form).filter(feature => feature.source.classId === 'bard')
      .map(feature => ({...feature, class:'Bard', subclass:'College of Lore', hideUseButton:true,
        ...(feature.id === BONUS_PROFICIENCIES_FEATURE_ID ? { isBonusProficienciesConfig: true } : {}),
        ...(feature.id === MAGICAL_DISCOVERIES_FEATURE_ID ? { isMagicalDiscoveriesConfig: true } : {}),
        ...(feature.id === CUTTING_WORDS_FEATURE_ID ? { description: `${feature.description} When an enemy hits, you are asked, unless the Cutting Words tile on the action bar is off.` } : {}),
        ...(feature.id === PEERLESS_SKILL_FEATURE_ID ? { description: `${feature.description} It is offered with the roll.` } : {})}));
    const druidSubclassFeatures = getSubclassFeatureEntitlements(form).filter(feature => feature.source.classId === 'druid')
      .map(feature => ({...feature, class:'Druid', subclass:'Circle of the Land', hideUseButton:true,
        ...(feature.id === CIRCLE_SPELLS_FEATURE_ID ? { isLandConfig: true } : {}),
        ...(feature.id === LANDS_AID_FEATURE_ID ? { description: `${feature.description} Use its tile on the action bar.` } : {}),
        ...(feature.id === NATURAL_RECOVERY_FEATURE_ID ? { description: `${feature.description} Its tile on the action bar casts the spell, and the Short Rest window offers the slots.` } : {})}));
    // Magical Discoveries: always prepared, cast with a Bard slot from their card or, with an engine, the action bar.
    const discoverySpellFeatures = getMagicalDiscoveries(form).map((spellId) => {
      const spell = spellCatalog.find((entry) => String(entry.name || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') === spellId);
      const name = spell?.name || spellId, level = Number(spell?.level) || 0;
      return { id: discoveryGrantId(spellId), name, class: 'Bard', subclass: 'College of Lore', level: 6,
        meta: `Magical Discovery · always prepared · ${level ? `level ${level}` : 'cantrip'}`, description: spell?.description || '',
        ...(level ? { oathSpell: { spellId, name, baseLevel: level, castingTime: spell?.castingTime || '1 action', actionBar: Boolean(getBuffSpellDefinition(spellId) || getHealingSpellDefinition(spellId)) } } : { hideUseButton: true }) };
    });
    // Life Domain spells: always prepared, cast with a Cleric slot from their card or, with an engine, the action bar.
    const domainSpellFeatures = getLifeDomainSpells(form).map((spellId) => {
      const spell = spellCatalog.find((entry) => String(entry.name || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') === spellId);
      const name = spell?.name || spellId;
      const gained = Number(Object.entries(LIFE_DOMAIN_SPELLS).find(([, spells]) => spells.includes(spellId))?.[0]) || 3;
      return { id: domainSpellGrantId(spellId), name, class: 'Cleric', subclass: 'Life Domain', level: gained,
        meta: `Domain spell · always prepared · level ${Number(spell?.level) || 1}`, description: spell?.description || '',
        oathSpell: { spellId, name, baseLevel: Number(spell?.level) || 1, castingTime: spell?.castingTime || '1 action', actionBar: Boolean(getBuffSpellDefinition(spellId) || getHealingSpellDefinition(spellId)) } };
    });
    // Oath of Devotion spells: always prepared, cast with a Paladin slot from their card (Shield of Faith also from the action bar).
    const oathSpellFeatures = getDevotionOathSpells(form).map((spellId) => {
      const spell = spellCatalog.find((entry) => String(entry.name || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') === spellId);
      const name = spell?.name || spellId;
      return { id: oathSpellGrantId(spellId), name, class: 'Paladin', subclass: 'Oath of Devotion', level: Number(spell?.level) >= 2 ? 5 : 3,
        meta: `Oath spell · always prepared · level ${Number(spell?.level) || 1}`, description: spell?.description || '',
        oathSpell: { spellId, name, baseLevel: Number(spell?.level) || 1, castingTime: spell?.castingTime || '1 action', actionBar: Boolean(getBuffSpellDefinition(spellId)) } };
    });
    // Faithful Steed (Paladin 5): Find Steed is always prepared, cast free once per Long Rest or with a slot.
    const findSteed = spellCatalog.find((entry) => entry.name === 'Find Steed');
    const faithfulSteedFeatures = hasPaladinFeature(form, 'faithfulSteed') ? [{ id: FAITHFUL_STEED_GRANT_ID, name: findSteed?.name || 'Find Steed', class: 'Paladin', level: 5,
      meta: 'Faithful Steed · always prepared · level 2', description: `${findSteed?.description || ''} Summoning is narrated: the DM places the steed's token.`.trim(),
      faithfulSteedSpell: { spellId: FIND_STEED_SPELL_ID, name: findSteed?.name || 'Find Steed', castingTime: findSteed?.castingTime || '1 action' } }] : [];
    // Cantrip Fighting Styles (Druidic Warrior for Ranger, Blessed Warrior for Paladin): each chosen cantrip is
    // castable at will from its action-bar tile.
    const styleCantripFeatures = getStyleCantripGrants(form).map(({ spellId, grantId, classId, styleName, ability }) => {
      const spell = spellCatalog.find((entry) => String(entry.name || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') === spellId);
      const name = spell?.name || spellId;
      return { id: grantId, name, class: classId.charAt(0).toUpperCase() + classId.slice(1), level: 2,
        meta: `${styleName} cantrip · ${ability.charAt(0).toUpperCase() + ability.slice(1)}`,
        description: spell?.description || '', styleCantripSpell: { spellId, name, styleName } };
    });
    // Magic Initiate (feat): a card per feat to choose its spells, then one card per chosen spell. Cantrips cast from
    // their action-bar tile; the level 1 spell casts free once per Long Rest or with a slot.
    const magicInitiateFeatures = getMagicInitiateLists(form).flatMap((list) => {
      const label = `Magic Initiate (${MAGIC_INITIATE_LISTS[list]})`;
      const choice = getMagicInitiateChoice(form, list);
      const config = { id: `feat-magic-initiate-${list}`, name: label, class: 'Feat', meta: `Feat · ${choice ? `${choice.ability.charAt(0).toUpperCase() + choice.ability.slice(1)} spellcasting` : 'choose spells'}`,
        description: `Two ${MAGIC_INITIATE_LISTS[list]} cantrips and one level 1 ${MAGIC_INITIATE_LISTS[list]} spell, always prepared. Cast the level 1 spell once per Long Rest without a slot, or with any spell slot. When you gain a level, you can replace one of these spells.`,
        hideUseButton: true, isMagicInitiateConfig: true, magicInitiateList: list };
      const spells = getMagicInitiateGrants(form).filter((grant) => grant.list === list).map(({ spellId, grantId, level }) => {
        const spell = spellCatalog.find((entry) => String(entry.name || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') === spellId);
        const name = spell?.name || spellId;
        return { id: grantId, name, class: 'Feat', meta: `${label} · ${level === 0 ? 'cantrip' : 'level 1 · always prepared'}`, description: spell?.description || '',
          magicInitiateSpell: { spellId, name, level, label, castingTime: spell?.castingTime || '1 action', actionBar: level === 0 || Boolean(getBuffSpellDefinition(spellId) || getHealingSpellDefinition(spellId)) } };
      });
      return [config, ...spells];
    });
    // One card per feat (Origin feats from the background or a Human's Versatile trait, and feats taken at a level).
    // Magic Initiate has its own cards above; Fighting Style feats show on the Fighting Style card.
    const featSourceLabel = (feat) => {
      const source = feat?._featSelection?.source;
      if (source === 'background') return form?.background?.name ? `${form.background.name} background` : 'Background';
      if (source === 'species') return 'Human Versatile';
      return null;
    };
    const featCards = (Array.isArray(form?.feat) ? form.feat : [])
      .filter((feat) => typeof feat?.featName === 'string' && feat.featName.trim()
        && feat.featName !== 'Magic Initiate' && feat.category !== 'Fighting Style')
      .map((feat, index) => {
        const category = feat.category || (feat._featSelection?.id === 'ability-score-improvement' ? 'General' : '');
        return { id: `feat-${String(feat._featSelection?.id || feat.featName).toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${index}`,
          name: feat.featName, class: 'Feat', isFeatCard: true,
          meta: [category ? `${category} feat` : 'Feat', featSourceLabel(feat)].filter(Boolean).join(' · '),
          description: feat.notes || feat.description || feat.desc || '', hideUseButton: true };
      });
    // Hide 2014 subclass placeholder rows; the subclass registry supplies the real features.
    return [...visibleFeatures, ...featCards, ...masteryAwareBarbarian, ...rogueMastery, ...subclassFeatures, ...druidSubclassFeatures, ...monkSubclassFeatures, ...fighterSubclassFeatures, ...rangerSubclassFeatures, ...clericSubclassFeatures, ...bardSubclassFeatures, ...discoverySpellFeatures, ...wizardSubclassFeatures, ...domainSpellFeatures, ...paladinSubclassFeatures, ...oathSpellFeatures, ...faithfulSteedFeatures, ...styleCantripFeatures, ...magicInitiateFeatures]
      .filter(isClassFeatureDisplayable).sort(
      (a, b) =>
        (a.class || '').localeCompare(b.class || '') ||
        (a.level || 0) - (b.level || 0)
    );
  }, [barbarianLevel, features, form, masteryEntitlements]);

  const displayFeatures = useMemo(() => {
    if (ancestryFeatures.length === 0) return classFeatures;
    return [...ancestryFeatures, ...classFeatures];
  }, [ancestryFeatures, classFeatures]);


  const displayedWeaponMasteryState = useMemo(() => {
    if (eligibleWeaponMasteryMap.size === 0) return weaponMasteryState;
    return {
      ...weaponMasteryState,
      selections: weaponMasteryState.selections.filter((key) =>
        eligibleWeaponMasteryMap.has(key)
      ),
    };
  }, [eligibleWeaponMasteryMap, weaponMasteryState]);

  const handleWeaponMasteryChange = useCallback(
    (slotIndex, nextKey) => {
      if (!onCharacterChange) return;
      const activeSelections = Array.from(
        { length: displayedWeaponMasteryState.count },
        (_, index) => displayedWeaponMasteryState.selections[index] || ''
      );
      activeSelections[slotIndex] = nextKey;
      const selectedKeys = activeSelections.filter(Boolean);
      const selectedWeapons = selectedKeys.map((key) => eligibleWeaponMasteryMap.get(key) || key);
      const validation = validateBarbarianWeaponMasteries(form, selectedWeapons);
      if (validation.valid.length !== selectedKeys.length || validation.hasDuplicates) return;
      onCharacterChange((previous) => setWeaponMasterySelections(previous || form, selectedWeapons));
    },
    [eligibleWeaponMasteryMap, form, onCharacterChange, displayedWeaponMasteryState]
  );

  const renderWeaponMasteryDetails = useCallback((sourceId = 'barbarian') => {
    const entitlement = masteryEntitlements.find((entry) => entry.sourceId === sourceId);
    if (!entitlement) return <p>No Weapon Mastery entitlement.</p>;
    const options = Object.entries(weaponData || {}).filter(([key]) => entitlement.eligibleWeaponKeys.includes(String(key).toLowerCase())).map(([key, weapon]) => ({ key: String(key).toLowerCase(), ...weapon })).sort((a, b) => a.name.localeCompare(b.name));
    const selections = getSelectionsForSource(form, sourceId);
    const replacementsAvailable = Math.max(0, Number(form?.weaponMasteries?.sources?.[sourceId]?.replacementsAvailable) || 0);
    const isConfigured = selections.length >= entitlement.count;
    const activeSelections = Array.from(
      { length: entitlement.count }, (_, index) => selections[index] || ''
    );
    const activeSet = new Set(activeSelections.filter(Boolean));
    const persist = async (slotIndex, nextKey) => {
      const next = [...activeSelections]; next[slotIndex] = nextKey;
      const requested = next.filter(Boolean);
      if (new Set(requested).size !== requested.length) return;
      const response = await apiFetch(`/characters/${form._id || characterId}/weapon-masteries/${sourceId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ selections: requested })
      });
      if (!response?.ok) return;
      const weaponMasteries = await response.json();
      onCharacterChange?.((previous) => {
        const base = previous || form;
        const updated = { ...base, weaponMasteries };
        // Keep the deprecated Barbarian mirror during the compatibility window.
        return sourceId === 'barbarian' ? { ...updated, classState: { ...(base.classState || {}), barbarian: { ...(base.classState?.barbarian || {}), weaponMasteries: { ...(base.classState?.barbarian?.weaponMasteries || {}), selections: requested } } } } : updated;
      });
    };
    return (
      <div className="text-start">
        <p className="feature-sheet-hint">
          {!isConfigured
            ? `Choose ${entitlement.count - selections.length} more weapon${entitlement.count - selections.length === 1 ? '' : 's'}.`
            : replacementsAvailable > 0
              ? `You may replace ${replacementsAvailable === 1 ? 'one mastery selection' : `${replacementsAvailable} mastery selections`}.`
              : 'Selections can be changed after a Long Rest.'}
        </p>
        {Array.from({ length: entitlement.count }).map((_, index) => {
          const selectedKey = activeSelections[index] || '';
          return (
            <Form.Group className="mb-3" controlId={`${sourceId}-weapon-mastery-${index}`} key={index}>
              <Form.Label>{`Mastery ${index + 1}`}</Form.Label>
              <Form.Select
                aria-label={`Weapon Mastery ${index + 1}`}
                value={selectedKey}
                onChange={(event) => persist(index, event.target.value)}
                disabled={!onCharacterChange || (isConfigured && replacementsAvailable < 1)}
              >
                <option value="">Select weapon</option>
                {options.map((weapon) => {
                  const disabled = activeSet.has(weapon.key) && weapon.key !== selectedKey;
                  return (
                    <option value={weapon.key} disabled={disabled} key={weapon.key}>
                      {weapon.name}
                      {weapon.mastery ? ` (${weapon.mastery})` : ''}
                    </option>
                  );
                })}
              </Form.Select>
            </Form.Group>
          );
        })}

      </div>
    );
  }, [characterId, form, masteryEntitlements, onCharacterChange, weaponData]);

  const pendingFreeCastConfig = pendingSpell?.freeCast || null;
  const pendingFreeCastRemaining = pendingFreeCastConfig
    ? typeof pendingFreeCastConfig.getRemaining === 'function'
      ? pendingFreeCastConfig.getRemaining()
      : Number.isFinite(pendingFreeCastConfig.remaining)
      ? Math.max(0, Math.floor(pendingFreeCastConfig.remaining))
      : 0
    : 0;

  useEffect(() => {
    if (!showFeatures || !hasWeaponMasteryEntitlement) return;
    let cancelled = false;
    async function fetchWeapons() {
      try {
        const res = await apiFetch('/weapons');
        if (!cancelled && res?.ok) {
          const data = await res.json();
          setWeaponData(data || {});
        }
      } catch (err) {
        // Weapon Mastery uses the centralized weapon API when it is available.
      }
    }
    fetchWeapons();
    return () => {
      cancelled = true;
    };
  }, [showFeatures, hasWeaponMasteryEntitlement]);

  useEffect(() => {
    if (!showFeatures) return;
    async function fetchFeatures() {
      setLoading(true);
      setError(null);
      const allFeatures = [];
      try {
        for (const occ of Array.isArray(form.occupation) ? form.occupation : []) {
          if (typeof occ !== 'object' || occ === null) continue;
          const displayName = occ.Name || occ.Occupation || occ.name || '';
          const className = displayName.toLowerCase();
          if (!className) continue;
          for (let lvl = 1; lvl <= (occ.Level || 1); lvl++) {
            const res = await apiFetch(`/classes/${className}/features/${lvl}`);
            if (!res.ok) continue;
            const data = await res.json();
            (data.features || []).forEach((f) =>
              allFeatures.push({ ...f, class: displayName, level: lvl })
            );
          }
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
        setError('Unable to load class features');
      } finally {
        allFeatures.sort(
          (a, b) =>
            (a.class || '').localeCompare(b.class || '') ||
            (a.level || 0) - (b.level || 0)
        );
        setFeatures(allFeatures);
        setLoading(false);
      }
    }
    fetchFeatures();
  }, [form.occupation, showFeatures]);

  const closePendingSpellModal = useCallback(() => {
    setShowUpcast(false);
    setPendingSpell(null);
  }, []);

  const handlePendingSpellFreeCast = async () => {
    if (!pendingSpell?.freeCast || pendingSpell.freeCast.getRemaining() <= 0) return false;
    return onCastSpell?.({ featureGrantId: pendingSpell.id, mode: 'feature', baseLevel: pendingSpell.baseLevel,
      level: pendingSpell.baseLevel, name: pendingSpell.name, castingTime: pendingSpell.castingTime });
  };
  const handlePendingSpellSlotCast = async (level, slotType) => {
    if (!pendingSpell) return false;
    return onCastSpell?.({ featureGrantId: pendingSpell.id, baseLevel: pendingSpell.baseLevel,
      level: pendingSpell.baseLevel, slotLevel: level, slotType, name: pendingSpell.name, castingTime: pendingSpell.castingTime });
  };

  const dialogClassName = useMemo(() => {
    if (!isDocked) {
      return undefined;
    }

    const classes = ['docked-modal'];
    if (dockedSide) {
      classes.push(`docked-modal--${dockedSide}`);
    }
    classes.push('docked-modal--features');
    return classes.join(' ');
  }, [isDocked, dockedSide]);

  const modalClassName = useMemo(() => {
    const classes = ['dnd-modal', 'modern-modal'];
    if (isDocked) {
      classes.push('docked-modal-container');
    }
    return classes.join(' ');
  }, [isDocked]);


  const abilityCodexItems = useMemo(() => {
    const signatureNames = new Set([
      'rage', 'action surge', 'second wind', 'lay on hands', 'wild shape',
      'channel divinity', 'ki', 'sorcery points', 'sneak attack',
      'reckless attack', 'adrenaline rush', 'large form', 'draconic flight'
    ]);

    const normalize = (value) => String(value || '').toLowerCase();
    const inferType = (feat) => {
      const text = `${feat?.name || ''} ${feat?.meta || ''} ${feat?.description || feat?.desc || ''} ${feat?.type || ''}`.toLowerCase();
      if (feat?.class === 'Feat') return 'feat';
      if (feat?.subclass || text.includes('subclass')) return 'subclass';
      if (feat?.id?.includes('race') || ['dwarf', 'orc', 'elf', 'gnome', 'tiefling', 'dragonborn', 'goliath', 'halfling'].some((race) => feat?.id?.startsWith(race) || normalize(feat?.meta).includes(race))) return 'racial';
      if (feat?.hideUseButton || feat?.type === 'passive' || /advantage|resistance|proficiency|increases|you know|always/.test(text)) return 'passive';
      if (/reaction|bonus action|action|attack|combat|surge|rage|dash|cast/.test(text)) return 'combat';
      return 'utility';
    };
    const inferAction = (feat) => {
      const text = `${feat?.name || ''} ${feat?.description || feat?.desc || ''}`.toLowerCase();
      if (text.includes('reaction')) return 'Reaction';
      if (text.includes('bonus action')) return 'Bonus Action';
      if (text.includes('action')) return 'Action';
      return 'Passive';
    };
    const inferRecharge = (feat) => {
      const text = `${feat?.description || feat?.desc || ''} ${feat?.meta || ''}`.toLowerCase();
      if (text.includes('short rest')) return 'Short Rest';
      if (text.includes('long rest')) return 'Long Rest';
      if (feat?.hideUseButton || feat?.type === 'passive') return 'Always Active';
      return 'At Will';
    };
    const getUses = (feat) => {
      if (String(feat?.class).toLowerCase() === 'fighter' && ['Second Wind','Tactical Mind'].includes(feat?.name)) { const pool=getSecondWindProfile(form); return `${pool.remaining} / ${pool.maximum}`; }
      if (feat?.isFightingStyleConfig) return (feat.fightingStyleSlot === 'additional' ? getAdditionalFightingStyle(form) : getClassFightingStyle(form, feat.fightingStyleClass || 'fighter'))?.name || 'Choose style';
      if (feat?.isHuntersPreyConfig) return getHuntersPrey(form)?.name || 'Choose prey';
      if (feat?.isDefensiveTacticsConfig) return getDefensiveTactics(form)?.name || 'Choose tactics';
      if (feat?.isDivineOrderConfig) return getDivineOrder(form)?.name || 'Choose order';
      if (feat?.isBlessedStrikesConfig) return getBlessedStrikes(form)?.name || 'Choose option';
      if (feat?.isPrimalOrderConfig) return getPrimalOrder(form)?.name || 'Choose order';
      if (feat?.isElementalFuryConfig) return getElementalFury(form)?.name || 'Choose option';
      if (feat?.isKnownFormsConfig) return getKnownForms(form).length ? 'Chosen' : 'Choose forms';
      if (feat?.isLandConfig) return getLand(form)?.name || 'Choose land';
      if (feat?.isSpellbookConfig) return 'Spellbook';
      if (feat?.isSpellMasteryConfig) return getSpellMastery(form) ? 'Chosen' : 'Choose spells';
      if (feat?.isSignatureSpellsConfig) return getSignatureSpells(form).length ? 'Chosen' : 'Choose spells';
      if (feat?.isBonusProficienciesConfig) return getBonusProficiencies(form).length ? 'Chosen' : 'Choose skills';
      if (feat?.isMagicalDiscoveriesConfig) return getMagicalDiscoveries(form).length ? 'Chosen' : 'Choose spells';
      if (feat?.isMagicInitiateConfig) return getMagicInitiateChoice(form, feat.magicInitiateList) ? 'Spells chosen' : 'Choose spells';
      if (feat?.magicInitiateSpell?.level === 1) return `${magicInitiateFreeCastsRemaining(form, feat.id)} / ${MAGIC_INITIATE_FREE_CASTS}`;
      if (String(feat?.class).toLowerCase() === 'ranger' && feat?.name === 'Favored Enemy') {
        const maximum = favoredEnemyUses(getRangerLevel(form));
        const used = Number(form?.spellcastingState?.speciesFreeCasts?.[FAVORED_ENEMY_GRANT_ID]) || 0;
        return `${Math.max(0, maximum - used)} / ${maximum}`;
      }
      if (feat?.name?.includes('Action Surge')) { const pool=getActionSurgeProfile(form); return `${pool.remaining} / ${pool.maximum}`; }
      if (feat?.id === 'orc-adrenaline-rush') return `${adrenalineRushUses} / ${adrenalineRushMaxUses}`;
      if (feat?.id === 'gnome-forest-speak-with-animals') return `${speakWithAnimalsUses} / ${speakWithAnimalsMaxUses}`;
      if (LIMITED_USE_LINEAGE_SPELL_IDS_SET.has(feat?.id)) return `${lineageSpellUses[feat.id] ?? 0} / ${LINEAGE_SPELLS[feat.id]?.maxUses ?? 1}`;
      if (feat?.id === 'goliath-large-form') return largeFormUsed ? 'Spent' : 'Ready';
      if (feat?.id === 'dragonborn-draconic-flight') return draconicFlightUsed ? 'Spent' : 'Ready';
      if (feat?.isWeaponMasteryConfig) {
        const grant = masteryEntitlements.find((entry) => entry.sourceId === (feat.masterySource || 'barbarian'));
        return `${getSelectionsForSource(form, feat.masterySource || 'barbarian').length} / ${grant?.count || 0}`;
      }
      return feat?.hideUseButton || feat?.type === 'passive' ? 'Always On' : 'Available';
    };

    return displayFeatures.map((feat, idx) => {
      const key = feat.id || `${feat.name}-${idx}`;
      const type = inferType(feat);
      const source = feat.class || (feat.meta ? String(feat.meta).split('•')[0].trim() : 'Character');
      const name = feat.name || 'Feature';
      const searchText = `${name} ${source} ${feat.meta || ''} ${feat.description || feat.desc || ''} ${type}`.toLowerCase();
      const recentlyUsedIndex = recentAbilityKeys.indexOf(key);
      return {
        feat, key, idx, type, source, name, searchText,
        recentlyUsedIndex,
        actionType: inferAction(feat),
        recharge: inferRecharge(feat),
        usesLabel: getUses(feat),
        isSignature: signatureNames.has(name.toLowerCase().replace(/ \(.*\)$/, '')),
        icon: type === 'combat' ? '⚔️' : type === 'passive' ? '🛡️' : type === 'racial' ? '🍃' : type === 'subclass' ? '✦' : type === 'feat' ? '★' : '✧',
      };
    });
  }, [displayFeatures, recentAbilityKeys, surgeUsed, adrenalineRushUses, adrenalineRushMaxUses, speakWithAnimalsUses, speakWithAnimalsMaxUses, lineageSpellUses, largeFormUsed, draconicFlightUsed, displayedWeaponMasteryState, masteryEntitlements, form]);

  // What a feature tile (and its details sheet) shows: source line, action line, choice glow and one status.
  const summarizeAbility = (item) => {
    const feat = item.feat;
    const choiceKey = featureChoiceKey(feat);
    const choiceState = !choiceKey ? ''
      : pendingFeatureChoices.has(choiceKey) ? 'ability-card--needs-choice'
        : editableFeatureChoices.has(choiceKey) ? 'ability-card--editable' : '';
    const needsChoice = choiceState === 'ability-card--needs-choice';
    const sourceParts = (feat.meta ? [feat.meta] : [feat.class, feat.level != null ? `Level ${feat.level}` : null, feat.subclass]).filter(Boolean);
    const actionParts = item.actionType === 'Passive' ? []
      : [item.actionType !== 'Active' ? item.actionType : null, item.recharge !== 'At Will' ? item.recharge : null].filter(Boolean);
    const usesMatch = String(item.usesLabel).match(/^(\d+) \/ (\d+)$/);
    const status = feat.isFightingStyleConfig || feat.isHuntersPreyConfig || feat.isDefensiveTacticsConfig || feat.isDivineOrderConfig || feat.isBlessedStrikesConfig || feat.isPrimalOrderConfig || feat.isElementalFuryConfig || feat.isKnownFormsConfig || feat.isLandConfig || feat.isSpellbookConfig || feat.isSpellMasteryConfig || feat.isSignatureSpellsConfig || feat.isBonusProficienciesConfig || feat.isMagicalDiscoveriesConfig || feat.isMagicInitiateConfig
      ? (needsChoice ? { tone: 'needs', text: feat.isHuntersPreyConfig ? 'Choose prey' : feat.isDefensiveTacticsConfig ? 'Choose tactics' : feat.isDivineOrderConfig || feat.isPrimalOrderConfig ? 'Choose order' : feat.isBlessedStrikesConfig || feat.isElementalFuryConfig ? 'Choose option' : feat.isKnownFormsConfig ? 'Choose forms' : feat.isLandConfig ? 'Choose land' : feat.isSpellbookConfig ? 'Add spells' : feat.isSpellMasteryConfig || feat.isSignatureSpellsConfig || feat.isMagicalDiscoveriesConfig ? 'Choose spells' : feat.isBonusProficienciesConfig ? 'Choose skills' : feat.isMagicInitiateConfig ? 'Choose spells' : 'Choose style' } : { tone: 'choice', text: item.usesLabel })
      : feat.isWeaponMasteryConfig
        ? { tone: needsChoice ? 'needs' : 'choice', text: `${item.usesLabel} selected` }
        : usesMatch
          ? { tone: Number(usesMatch[1]) > 0 ? 'ready' : 'spent', text: `${usesMatch[1]} / ${usesMatch[2]} left` }
          : item.usesLabel === 'Spent'
            ? { tone: 'spent', text: 'Spent' }
            : item.actionType === 'Passive'
              ? { tone: 'passive', text: 'Always on' }
              : { tone: 'ready', text: 'Ready' };
    return { choiceState, sourceParts, actionParts, status };
  };

  const modalAbilityItem = modalFeature
    ? abilityCodexItems.find((entry) => entry.feat === modalFeature)
      || abilityCodexItems.find((entry) => (modalFeature.id ? entry.feat.id === modalFeature.id : entry.name === modalFeature.name))
    : null;
  const modalAbilitySummary = (() => {
    if (!modalAbilityItem) return null;
    const { sourceParts, actionParts, status } = summarizeAbility(modalAbilityItem);
    return { icon: modalAbilityItem.icon, sourceParts, chips: [...actionParts.map((text) => ({ text })), status] };
  })();

  const visibleAbilityItems = useMemo(() => {
    const query = abilitySearch.trim().toLowerCase();
    const filtered = abilityCodexItems.filter((item) => {
      const matchesSearch = !query || item.searchText.includes(query);
      const matchesFilter = activeAbilityFilter === 'all' ||
        (activeAbilityFilter === 'active' && item.actionType !== 'Passive') ||
        (activeAbilityFilter === 'passive' && item.actionType === 'Passive') ||
        (activeAbilityFilter === 'recent' && item.recentlyUsedIndex !== -1) ||
        (activeAbilityFilter === 'class' && Boolean(item.feat.class) && item.type !== 'feat') ||
        item.type === activeAbilityFilter;
      return matchesSearch && matchesFilter;
    });
    return [...filtered].sort((a, b) => {
      if (abilitySort === 'alphabetical') return a.name.localeCompare(b.name);
      if (abilitySort === 'level') return (a.feat.level || 0) - (b.feat.level || 0) || a.name.localeCompare(b.name);
      if (abilitySort === 'combat') return Number(b.type === 'combat') - Number(a.type === 'combat') || a.name.localeCompare(b.name);
      if (abilitySort === 'recent') return (a.recentlyUsedIndex === -1 ? 999 : a.recentlyUsedIndex) - (b.recentlyUsedIndex === -1 ? 999 : b.recentlyUsedIndex);
      return a.source.localeCompare(b.source) || (a.feat.level || 0) - (b.feat.level || 0) || a.name.localeCompare(b.name);
    });
  }, [abilityCodexItems, abilitySearch, activeAbilityFilter, abilitySort]);

  const selectedAbility = useMemo(() => {
    return abilityCodexItems.find((item) => item.key === selectedAbilityKey) || null;
  }, [abilityCodexItems, selectedAbilityKey]);

  const markRecentlyUsed = useCallback((key) => {
    setRecentAbilityKeys((prev) => [key, ...prev.filter((item) => item !== key)].slice(0, 8));
  }, []);

  const handleModalHide = useCallback(() => {
    if (isDocked) {
      if (typeof onDockClose === 'function') {
        onDockClose();
      }
      return;
    }

    handleCloseFeatures?.();
  }, [handleCloseFeatures, isDocked, onDockClose]);

  return (
    <>
      <Modal
        className={modalClassName}
        show={showFeatures}
        onHide={handleModalHide}
        size="xl"
        centered={!isDocked}
        backdrop={isDocked ? false : true}
        enforceFocus={!isDocked}
        restoreFocus={!isDocked}
        dialogClassName={dialogClassName}
      >
        <Card className="modern-card abilities-codex-card">
            <Card.Header className="modal-header">
              <DockControls
                dockedSide={dockedSide}
                onDockChange={onDockChange}
                isDocked={isDocked}
              />
              <Card.Title className="modal-title">Character Features</Card.Title>
            </Card.Header>
            <Card.Body className="abilities-codex-body">
            <SpellRecoveryControls form={form} onCharacterChange={onCharacterChange} />
              {error && (
                <div className="text-danger mb-2">{error}</div>
              )}
              {loading ? (
                <div className="d-flex justify-content-center py-4">
                  <Spinner animation="border" role="status" />
                </div>
              ) : abilityCodexItems.length > 0 ? (
                <div className="abilities-codex-shell">
                  <div className="abilities-codex-toolbar">
                    <div className="abilities-codex-kicker">Hero Ability Codex</div>
                    <div className="abilities-codex-title-row">
                      <div>
                        <h2>Character Features</h2>
                        <p>Scan active powers, passive traits, class resources, and lineage gifts without losing any rules detail.</p>
                      </div>
                      <div className="abilities-codex-count" aria-label={`${visibleAbilityItems.length} abilities shown out of ${abilityCodexItems.length}`}>
                        <strong>{visibleAbilityItems.length}</strong>
                        <span>/ {abilityCodexItems.length} Abilities</span>
                      </div>
                    </div>
                  </div>
                  <div className="abilities-codex-layout">
                    <aside className="abilities-codex-sidebar" aria-label="Ability filters">
                      {[
                        ['all', 'All Features'], ['combat', 'Combat'], ['passive', 'Passive'], ['active', 'Active'],
                        ['utility', 'Utility'], ['class', 'Class Features'], ['racial', 'Racial Features'],
                        ['subclass', 'Subclass Features'], ['feat', 'Feats'], ['recent', 'Recently Used'],
                      ].map(([filter, label]) => (
                        <button
                          type="button"
                          key={filter}
                          className={`abilities-filter-pill ${activeAbilityFilter === filter ? 'is-active' : ''}`}
                          onClick={() => setActiveAbilityFilter(filter)}
                        >
                          <span>{label}</span>
                          <Badge bg="dark">{filter === 'all' ? abilityCodexItems.length : abilityCodexItems.filter((item) => filter === 'active' ? item.actionType !== 'Passive' : filter === 'recent' ? item.recentlyUsedIndex !== -1 : filter === 'class' ? Boolean(item.feat.class) && item.type !== 'feat' : item.type === filter).length}</Badge>
                        </button>
                      ))}
                    </aside>
                    <div className="feature-card-grid abilities-grid">
                  {visibleAbilityItems.map((item, idx) => {
                    const feat = item.feat;
                    const featKey = item.key;
                    const isActionSurge = !feat.hideUseButton && feat.name?.includes('Action Surge');
                    const isDraconicFlight =
                      feat.id === 'dragonborn-draconic-flight';
                    const isAdrenalineRush = feat.id === 'orc-adrenaline-rush';
                    const isSpeakWithAnimals =
                      feat.id === 'gnome-forest-speak-with-animals';
                    const isRecklessAttackToggle = Boolean(feat.isRecklessAttackToggle);
                    const { choiceState, sourceParts, actionParts, status } = summarizeAbility(item);
                    const metaParts = [...sourceParts, ...actionParts];
                    const openFeatureDetails = () => {
                      setSelectedAbilityKey(featKey);
                      setModalFeature(feat);
                      setShowModal(true);
                    };
                    const lineageSpellConfig =
                      LINEAGE_SPELLS[feat.id] || null;
                    const lineageSpellHasLimitedUses =
                      LIMITED_USE_LINEAGE_SPELL_IDS_SET.has(feat.id);
                    const isLineageSpell = Boolean(lineageSpellConfig);
                    const lineageSpellRemainingUses = lineageSpellHasLimitedUses
                      ? lineageSpellUses[feat.id] ?? 0
                      : null;
                    const lineageSpellBaseLevelRaw = Number(
                      feat.lineageSpellBaseLevel ?? lineageSpellConfig?.baseLevel
                    );
                    const lineageSpellBaseLevel = Number.isFinite(
                      lineageSpellBaseLevelRaw
                    )
                      ? Math.max(1, Math.floor(lineageSpellBaseLevelRaw))
                      : 1;
                    const lineageSpellCastingTime =
                      feat.lineageSpellCastingTime ||
                      lineageSpellConfig?.castingTime ||
                      '1 action';
                    const lineageSpellDisplayName =
                      lineageSpellConfig?.spellName ||
                      feat.lineageSpellName ||
                      feat.name;
                    const hasSlotsForLineageSpell = hasAvailableSlotOfLevel(
                      lineageSpellBaseLevel
                    );
                    return (
                      <div className={`feature-card ability-card ability-tile ability-card--${item.type} ${item.isSignature ? 'ability-card--signature' : ''} ${selectedAbility?.key === featKey ? 'is-selected' : ''} ${choiceState}`} key={featKey} role="button" tabIndex={0} aria-label={`${feat.name} details`} onClick={(event) => { if (!event.target.closest('button, input, select, a')) openFeatureDetails(); }} onKeyDown={(event) => { if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); openFeatureDetails(); } }}>
                        <div className="ability-card-accent" aria-hidden="true" />
                        <div className="ability-card-icon" aria-hidden="true">{item.icon}</div>
                        <div className="ability-tile-text">
                          <div className="feature-card-name">{feat.name}</div>
                          <div className="feature-card-meta" title={metaParts.join(' · ')}>
                            {metaParts.map((part) => <span key={part}>{part}</span>)}
                          </div>
                        </div>
                          <div className="feature-card-actions">
                            {isActionSurge ? (
                              <Button
                                aria-label="use feature"
                                variant="link"
                                className={`p-0 border-0 ${surgeUsed ? 'opacity-50' : ''}`}
                                onClick={() => {
                                  if (!surgeUsed) {
                                    window.dispatchEvent(new CustomEvent('realmtracker:action-surge'));
                                    markRecentlyUsed(featKey);
                                  }
                                }}
                                disabled={surgeUsed}
                              >
                                <img
                                  src={actionSurgeIcon}
                                  alt="Action Surge"
                                  width={36}
                                  height={36}
                                />
                              </Button>
                            ) : isAdrenalineRush ? (
                              <Button
                                aria-label="use feature"
                                variant="link"
                                className={`p-0 border-0 ${
                                  adrenalineRushUses <= 0 ? 'opacity-50' : ''
                                }`}
                                onClick={() => recordSpeciesUse('orc-adrenaline-rush', onAdrenalineRush, featKey)}
                                disabled={speciesUseBusy || adrenalineRushUses <= 0}
                              >
                                <img
                                  src={adrenalineRushIcon}
                                  alt="Adrenaline Rush"
                                  width={36}
                                  height={36}
                                />
                              </Button>
                            ) : isDraconicFlight ? (
                              <Button
                                aria-label="use feature"
                                variant="link"
                                className={`p-0 border-0 ${
                                  draconicFlightUsed ? 'opacity-50' : ''
                                }`}
                                onClick={() => recordSpeciesUse('dragonborn-draconic-flight', onDraconicFlight, featKey)}
                                disabled={speciesUseBusy || draconicFlightUsed}
                              >
                                <img
                                  src={dragonWingsIcon}
                                  alt="Draconic Flight"
                                  width={36}
                                  height={36}
                                />
                              </Button>
                            ) : isLineageSpell ? (
                              <Button
                                aria-label={`cast ${
                                  lineageSpellConfig?.spellName || 'lineage spell'
                                } from lineage`}
                                variant="link"
                                className={`p-0 border-0 ${
                                  lineageSpellHasLimitedUses &&
                                  lineageSpellRemainingUses <= 0
                                    ? 'opacity-50'
                                    : ''
                                }`}
                                onClick={() => {
                                  if (!lineageSpellConfig?.spellName) return;
                                  const baseLevel = lineageSpellHasLimitedUses ? lineageSpellBaseLevel : 0;
                                  if (!baseLevel) {
                                    onCastSpell?.({ featureGrantId: feat.id, level: 0, name: lineageSpellDisplayName, castingTime: lineageSpellCastingTime })
                                      ?.catch(error => setError(error.message));
                                    return;
                                  }
                                  setPendingSpell({ id: feat.id, name: lineageSpellDisplayName, baseLevel, castingTime: lineageSpellCastingTime,
                                    freeCast: { label: 'C', remainingLabel: 'Uses remaining',
                                      ariaLabel: `Cast ${lineageSpellDisplayName} without expending a spell slot`,
                                      getRemaining: () => lineageSpellUsesRef.current[feat.id] || 0 } });
                                  setShowUpcast(true);
                                }}
                                disabled={lineageSpellHasLimitedUses && lineageSpellRemainingUses <= 0 && !hasSlotsForLineageSpell}
                              >
                                <i className="fa-solid fa-wand-sparkles" />
                              </Button>
                            ) : feat.faithfulSteedSpell ? (
                              <Button aria-label="cast Find Steed" variant="link" className="p-0 border-0"
                                onClick={() => { setPendingSpell({ id: feat.id, name: feat.faithfulSteedSpell.name, baseLevel: 2, castingTime: feat.faithfulSteedSpell.castingTime,
                                  freeCast: { label: 'F', remainingLabel: 'Free casts', ariaLabel: 'Cast Find Steed without expending a spell slot',
                                    getRemaining: () => faithfulSteedRemainingRef.current } }); setShowUpcast(true); }}>
                                <i className="fa-solid fa-horse" />
                              </Button>
                            ) : feat.oathSpell?.actionBar ? (
                              <span className="small text-nowrap" title={`Cast ${feat.oathSpell.name} from the spell list on the action bar.`}>Action bar</span>
                            ) : feat.oathSpell ? (
                              <Button aria-label={`cast ${feat.oathSpell.name} with a spell slot`} variant="link" className="p-0 border-0"
                                onClick={() => { setPendingSpell({ id: feat.id, name: feat.oathSpell.name, baseLevel: feat.oathSpell.baseLevel, castingTime: feat.oathSpell.castingTime }); setShowUpcast(true); }}>
                                <i className="fa-solid fa-wand-sparkles" />
                              </Button>
                            ) : feat.magicInitiateSpell?.actionBar ? (
                              <span className="small text-nowrap" title={`Cast ${feat.magicInitiateSpell.name} from its tile on the action bar.`}>Action bar</span>
                            ) : feat.magicInitiateSpell ? (
                              <Button aria-label={`cast ${feat.magicInitiateSpell.name}`} variant="link" className="p-0 border-0"
                                onClick={() => { setPendingSpell({ id: feat.id, name: feat.magicInitiateSpell.name, baseLevel: 1, castingTime: feat.magicInitiateSpell.castingTime,
                                  freeCast: { label: 'F', remainingLabel: 'Free casts', ariaLabel: `Cast ${feat.magicInitiateSpell.name} without expending a spell slot`,
                                    getRemaining: () => magicInitiateFreeCastsRemaining(magicInitiateFormRef.current, feat.id) } }); setShowUpcast(true); }}>
                                <i className="fa-solid fa-wand-sparkles" />
                              </Button>
                            ) : feat.styleCantripSpell ? (
                              <span className="small text-nowrap" title={`Cast ${feat.styleCantripSpell.name} from its ${feat.styleCantripSpell.styleName} tile on the action bar.`}>Action bar</span>
                            ) : isSpeakWithAnimals ? (
                              <div className="d-flex align-items-center gap-1">
                                <Button
                                  aria-label="cast Speak with Animals using a spell slot"
                                  variant="link"
                                  className="p-0 border-0"
                                  onClick={() => {
                                    setPendingSpell({ id: 'gnome-forest-speak-with-animals', name: 'Speak with Animals', baseLevel: 1, castingTime: '1 action',
                                      freeCast: { label: 'P', remainingLabel: 'Uses remaining',
                                        ariaLabel: 'Cast Speak with Animals using proficiency',
                                        getRemaining: () => speakWithAnimalsUsesRef.current || 0 } });
                                    setShowUpcast(true);
                                  }}
                                  disabled={!canUseSpeakWithAnimals || (speakWithAnimalsUses <= 0 && !speakWithAnimalsHasSlot)}
                                >
                                  <img
                                    src={speakWithAnimalIcon}
                                    alt="Speak with Animals"
                                    width={36}
                                    height={36}
                                  />
                                </Button>
                              </div>
                            ) : isRecklessAttackToggle ? (
                              <Button
                                aria-label="toggle Reckless Attack"
                                variant="link"
                                className="p-0 border-0"
                                title={reckless.active ? 'Reckless Attack is active until the start of your next turn.' : reckless.ready ? 'Reckless Attack is ready for your first attack roll this turn.' : canActivateRecklessAttack(form).reason || 'Ready Reckless Attack from the combat resource bar before your first attack roll.'}
                                disabled
                              >
                                <span className={`combat-hud-resource-tile__icon ${reckless.active || reckless.ready ? 'opacity-100' : 'opacity-50'}`}><Swords size={36} /></span>
                              </Button>
                            ) : null}
                          </div>
                        <span className={`ability-tile-status ability-tile-status--${status.tone}`}>{status.text}</span>
                      </div>
                    );
                  })}
                </div>
                    <aside className="ability-inspector" aria-label="Selected ability inspector">
                      {selectedAbility && (
                        <>
                          <div className={`ability-inspector-art ability-inspector-art--${selectedAbility.type}`}>
                            <span>{selectedAbility.icon}</span>
                          </div>
                          <div className="abilities-codex-kicker">Ability Inspector</div>
                          <h3>{selectedAbility.name}</h3>
                          <div className="ability-inspector-meta">
                            <span>{selectedAbility.source}</span>
                            {selectedAbility.feat.level != null && <span>Level {selectedAbility.feat.level}</span>}
                            <span>{selectedAbility.actionType}</span>
                          </div>
                          <p>{selectedAbility.feat.description || selectedAbility.feat.desc || 'No description available.'}</p>
                          <dl>
                            <div><dt>Uses</dt><dd>{selectedAbility.usesLabel}</dd></div>
                            <div><dt>Recharge</dt><dd>{selectedAbility.recharge}</dd></div>
                            <div><dt>Type</dt><dd>{selectedAbility.type}</dd></div>
                          </dl>
                          <div className="ability-inspector-actions">
                            <Button size="sm" className="action-btn" onClick={() => { setModalFeature(selectedAbility.feat); setShowModal(true); }}>Details</Button>
                          </div>
                        </>
                      )}
                    </aside>
                  </div>
                </div>
              ) : !error ? (
                <div className="text-center text-muted">No features found</div>
              ) : null}
            </Card.Body>
            <Card.Footer className="modal-footer abilities-codex-footer">
              {!loading && abilityCodexItems.length > 0 && (
                <div className="abilities-codex-controls abilities-codex-controls--footer">
                  <Form.Control
                    aria-label="Search character abilities"
                    className="abilities-codex-search"
                    placeholder="Search abilities, class, type, or rules text..."
                    value={abilitySearch}
                    onChange={(event) => setAbilitySearch(event.target.value)}
                  />
                  <Form.Select aria-label="Sort abilities" value={abilitySort} onChange={(event) => setAbilitySort(event.target.value)}>
                    <option value="class">Class</option>
                    <option value="level">Level</option>
                    <option value="recent">Recently Used</option>
                    <option value="alphabetical">Alphabetical</option>
                    <option value="combat">Combat</option>
                  </Form.Select>
                </div>
              )}
              <Button
                className="spellbook-modal-close-btn"
                onClick={handleModalHide}
              >
                Close
              </Button>
            </Card.Footer>
        </Card>
      </Modal>
      <FeatureModal
        summary={modalAbilitySummary}
        show={showModal}
        onHide={() => setShowModal(false)}
        feature={modalFeature?.isWeaponMasteryConfig
          ? { ...modalFeature, renderDetails: () => renderWeaponMasteryDetails(modalFeature.masterySource) }
          : modalFeature?.isFightingStyleConfig
            ? { ...modalFeature, renderDetails: () => <FightingStyleChooser form={form} classId={modalFeature.fightingStyleClass || 'fighter'} slot={modalFeature.fightingStyleSlot || 'class'} characterId={form?._id || form?.characterId || characterId} onCharacterChange={onCharacterChange}/> }
            : modalFeature?.isHuntersPreyConfig
              ? { ...modalFeature, renderDetails: () => <HuntersPreyChooser form={form} characterId={form?._id || form?.characterId || characterId} onCharacterChange={onCharacterChange}/> }
              : modalFeature?.isDefensiveTacticsConfig
              ? { ...modalFeature, renderDetails: () => <DefensiveTacticsChooser form={form} characterId={form?._id || form?.characterId || characterId} onCharacterChange={onCharacterChange}/> }
              : modalFeature?.isDivineOrderConfig
              ? { ...modalFeature, renderDetails: () => <DivineOrderChooser form={form} characterId={form?._id || form?.characterId || characterId} onCharacterChange={onCharacterChange}/> }
              : modalFeature?.isBlessedStrikesConfig
              ? { ...modalFeature, renderDetails: () => <BlessedStrikesChooser form={form} characterId={form?._id || form?.characterId || characterId} onCharacterChange={onCharacterChange}/> }
              : modalFeature?.isPrimalOrderConfig
              ? { ...modalFeature, renderDetails: () => <PrimalOrderChooser form={form} characterId={form?._id || form?.characterId || characterId} onCharacterChange={onCharacterChange}/> }
              : modalFeature?.isLandConfig
              ? { ...modalFeature, renderDetails: () => <LandChooser form={form} characterId={form?._id || form?.characterId || characterId} onCharacterChange={onCharacterChange}/> }
              : modalFeature?.isKnownFormsConfig
              ? { ...modalFeature, renderDetails: () => <KnownFormsChooser form={form} characterId={form?._id || form?.characterId || characterId} onCharacterChange={onCharacterChange}/> }
              : modalFeature?.isElementalFuryConfig
              ? { ...modalFeature, renderDetails: () => <ElementalFuryChooser form={form} characterId={form?._id || form?.characterId || characterId} onCharacterChange={onCharacterChange}/> }
              : modalFeature?.isSpellbookConfig
              ? { ...modalFeature, renderDetails: () => <WizardSpellbook form={form} characterId={form?._id || form?.characterId || characterId} onCharacterChange={onCharacterChange}/> }
              : modalFeature?.isSpellMasteryConfig
              ? { ...modalFeature, renderDetails: () => <SpellMasteryChooser form={form} characterId={form?._id || form?.characterId || characterId} onCharacterChange={onCharacterChange}/> }
              : modalFeature?.isSignatureSpellsConfig
              ? { ...modalFeature, renderDetails: () => <SignatureSpellsChooser form={form} characterId={form?._id || form?.characterId || characterId} onCharacterChange={onCharacterChange}/> }
              : modalFeature?.isBonusProficienciesConfig
              ? { ...modalFeature, renderDetails: () => <BonusProficienciesChooser form={form} characterId={form?._id || form?.characterId || characterId} onCharacterChange={onCharacterChange}/> }
              : modalFeature?.isMagicalDiscoveriesConfig
              ? { ...modalFeature, renderDetails: () => <MagicalDiscoveriesChooser form={form} characterId={form?._id || form?.characterId || characterId} onCharacterChange={onCharacterChange}/> }
              : modalFeature?.isMagicInitiateConfig
                ? { ...modalFeature, renderDetails: () => <MagicInitiateChooser key={modalFeature.magicInitiateList} form={form} list={modalFeature.magicInitiateList} characterId={form?._id || form?.characterId || characterId} onCharacterChange={onCharacterChange}/> }
                : modalFeature}
      />
      <UpcastModal
        show={showUpcast}
        onHide={closePendingSpellModal}
        baseLevel={
          Number.isFinite(Number(pendingSpell?.baseLevel))
            ? Math.max(1, Math.floor(Number(pendingSpell?.baseLevel)))
            : Number.isFinite(Number(pendingSpell?.level))
            ? Math.max(1, Math.floor(Number(pendingSpell?.level)))
            : 1
        }
        slots={availableSlots}
        onSelect={handlePendingSpellSlotCast}
        proficiencyAction={
          pendingFreeCastConfig
            ? {
                label: pendingFreeCastConfig.label || 'P',
                icon: pendingFreeCastConfig.icon,
                ariaLabel:
                  pendingFreeCastConfig.ariaLabel ||
                  'cast using proficiency feature',
                remainingText: pendingFreeCastConfig.remainingLabel
                  ? `${pendingFreeCastConfig.remainingLabel}: ${pendingFreeCastRemaining}`
                  : `Uses remaining: ${pendingFreeCastRemaining}`,
                disabled:
                  !!pendingFreeCastConfig.disabled ||
                  pendingFreeCastRemaining <= 0,
                onClick: handlePendingSpellFreeCast,
              }
            : undefined
        }
      />
    </>
  );
}


